import './App.css';
import { useState, useRef, useEffect } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginForm from "./LoginForm";
import RegistrationForm from "./RegistrationForm";
import EmailConfirmation from "./EmailConfirmation";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import ResetPassword from "./ResetPassword.jsx";
import ProjectRequests from "./ProjectRequests.jsx";
import AIRequests from "./AIRequests.jsx";
import { Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import AdminPanel from "./AdminPanel";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useDropzone } from "react-dropzone";
import React from "react";
import PDFDiagnostics from "./PDFDiagnostics";
import PurchaseCredits from "./PurchaseCredits.jsx";
import PaymentTracking from "./PaymentTracking.jsx";
import Help from "./Help.jsx";
import SendFeedback from "./SendFeedback.jsx";
import AboutZab from "./AboutZab.jsx";
import WorkflowBuilder from "./WorkflowBuilder";
import LoginClassic from "./LoginClassic";
import PublicHeader from "./PublicHeader";
import Settings from "./Settings.jsx";
import TerminologySettings from "./TerminologySettings.jsx";
import CustomWorkflow from "./CustomWorkflow.jsx";
import TMXSettings from "./TMXSettings.jsx";


// Upload limits
const LIMITS = {
  perFileMb: {
    ".pdf": 25, ".docx": 25, ".pptx": 25, ".idml": 25,
    ".xlsx": 20,
    ".html": 10, ".xml": 10, ".json": 10, ".yml": 10, ".yaml": 10,
    ".xliff": 10, ".xlf": 10, ".sdlxliff": 10, ".txt": 10,
  },
  maxFilesPerBatch: 10,
  totalBatchMb: 150,
};

const bytesFromMb = (mb) => Math.floor(mb * 1024 * 1024);
const ext = (name) => (name?.toLowerCase().match(/\.[^.]+$/)?.[0] ?? "");
const perFileCapBytes = (filename) => {
  const e = ext(filename);
  const caps = LIMITS.perFileMb;
  const mb = caps[e] ?? Math.min(...Object.values(caps)); // default to smallest cap
  return bytesFromMb(mb);
};

const FILE_TYPE_TO_EXTS = {
  "DOCX": [".docx"],
  "XLSX": [".xlsx"],
  "PPTX": [".pptx"],
  "HTML": [".html", ".htm"],
  "IDML": [".idml"],
  "TXT": [".txt"],
  "JSON": [".json"],
  "XML": [".xml"],
  "YML/YAML": [".yml", ".yaml"],
  "XLF/XLIFF": [".xlf", ".xliff"],
  "SDLXLIFF": [".sdlxliff"],
  "PDF": [".pdf"], // limited support
};

// Build reverse map from extension -> label (e.g., ".idml" -> "IDML")
const EXT_TO_LABEL = Object.entries(FILE_TYPE_TO_EXTS).reduce((acc, [label, list]) => {
  (list || []).forEach(e => { acc[e] = label; });
  return acc;
}, {});

// Default accept map used when Generic workflow (or no restriction)
const DEFAULT_DROPZONE_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/pdf': ['.pdf'],
  'application/json': ['.json'],
  'application/xml': ['.xml', '.xlf', '.xliff', '.sdlxliff'],
  'application/x-yaml': ['.yaml', '.yml'],
  'text/plain': ['.txt'],
  'text/html': ['.html', '.htm'],
  'application/octet-stream': ['.idml', '.sdlxliff'],
};


// Flatten DEFAULT_DROPZONE_ACCEPT into an extension list for client-side validation (UI only)
const DEFAULT_ACCEPTED_EXTS = Array.from(
  new Set(Object.values(DEFAULT_DROPZONE_ACCEPT).flat().map(e => String(e || "").toLowerCase()))
);


// Build a minimal dropzone "accept" object from an array of extensions
function buildAcceptFromExts(exts) {
  // Dropzone supports extension-only accept maps; one bucket is fine.
  return { 'application/octet-stream': Array.from(new Set(exts || [])) };
}


export function validateSelectedFiles(fileList) {
  const files = Array.from(fileList || []);
  if (!files.length) return { ok: false, message: "Please select at least one file." };

  if (files.length > LIMITS.maxFilesPerBatch) {
    return { ok: false, message: `Too many files. Max ${LIMITS.maxFilesPerBatch}.` };
  }

  let total = 0;
  for (const f of files) {
    const cap = perFileCapBytes(f.name);
    if (f.size > cap) {
      const mbCap = Math.floor(cap / (1024 * 1024));
      return { ok: false, message: `'${f.name}' exceeds per-file limit (${mbCap} MB).` };
    }
    total += f.size;
    if (total > bytesFromMb(LIMITS.totalBatchMb)) {
      return { ok: false, message: `Batch total exceeds ${LIMITS.totalBatchMb} MB.` };
    }
  }
  return { ok: true };
}


// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL && String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== 'undefined' && String(window.location.hostname).indexOf('ai-translation-frontend-sandbox') !== -1)
    ? 'https://ai-translation-backend-sandbox.onrender.com'
    : 'https://ai-translation-backend.onrender.com');

function codeEquiv(userValue, xliffValue, langNameToCode, langCodeMap) {
  if (!userValue || !xliffValue) return false;
  // If userValue is a pretty name, get code; if code, keep
  const userCode = langNameToCode[userValue] || userValue;
  // If xliffValue is a name, get code; if code, keep
  const xliffCode = langNameToCode[xliffValue] || xliffValue;
  // Normalize for case and dash/underscore
  const normUser = (userCode + "").toLowerCase().replace(/_/g, "-");
  const normXliff = (xliffCode + "").toLowerCase().replace(/_/g, "-");
  return normUser === normXliff;
}

function BillingSuccess() {
  const { jwt, user, logout, setUserAndTick, creditsTick } = useAuth();
  const [statusMsg, setStatusMsg] = React.useState("");
  const [capturing, setCapturing] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [orderId, setOrderId] = React.useState(null);
  const [provider, setProvider] = React.useState(null);
  // Restricted pairs from Custom Workflow
  const [workflowPairs, setWorkflowPairs] = React.useState([]); // [{source,target}]
  const [restrictMap, setRestrictMap] = React.useState({});     // { sourceName: Set([targetName,...]) }


  // Parse URL params once
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setProvider(params.get("provider"));
    setOrderId(
      params.get("token") || params.get("order_id") || params.get("orderId")
    );
  }, []);

  const refreshUserCredits = async () => {
    try {
      if (!jwt || !setUserAndTick) return;
      const res = await fetch(`${API_BASE}/me`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUserAndTick(data);
      }
    } catch (_) {}
  };

  // Stripe credits are finalized via webhook. After redirect back from Stripe,
  // poll /me briefly so the user's credits update without requiring a manual refresh.
  React.useEffect(() => {
    if (provider !== "stripe" || !jwt) return;

    let cancelled = false;
    let attempts = 0;
    const startCredits = typeof user?.credits === "number" ? user.credits : null;

    setStatusMsg("Payment received. Updating credits…");

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;

      await refreshUserCredits();

      // If credits changed (or we didn't know the starting credits), we can stop early.
      const nowCredits = typeof user?.credits === "number" ? user.credits : null;
      if (startCredits === null || (nowCredits !== null && nowCredits !== startCredits)) {
        setDone(true);
        setStatusMsg("Payment successful. Credits updated.");
        return;
      }

      if (attempts >= 8) {
        setDone(true);
        setStatusMsg("Payment successful. If your credits don’t update, refresh the page.");
        return;
      }

      setTimeout(tick, 1500);
    };

    tick();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [provider, jwt]);


  const capturePaypal = async () => {
    if (provider !== "paypal") {
      setStatusMsg("Payment recorded.");
      return;
    }
    if (!orderId) {
      setStatusMsg("Payment complete, but no PayPal order ID was returned.");
      return;
    }
    if (!jwt) {
      setStatusMsg("Please log in again to finalize your PayPal purchase.");
      return;
    }

    setCapturing(true);
    setStatusMsg("Finalizing your PayPal payment…");

    try {
      const r = await fetch(`${API_BASE}/billing/paypal/capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data?.detail || `Capture failed (${r.status})`);
      }

      // Credited (or already credited)
      setDone(true);
      if (data?.status === "already_credited") {
        setStatusMsg("Payment was already credited to your account.");
      } else {
        setStatusMsg("Payment captured and credits added to your account.");
      }
      await refreshUserCredits();
    } catch (err) {
      setStatusMsg(
        `Payment completed, but crediting failed: ${String(err.message || err)}`
      );
    } finally {
      setCapturing(false);
    }
  };

  // Auto-attempt capture once for PayPal (no redirect)
  React.useEffect(() => {
    if (provider === "paypal" && orderId && jwt && !done && !capturing) {
      capturePaypal();
    }
    // eslint-disable-next-line
  }, [provider, orderId, jwt]);
  return (
    <div className="mx-auto mt-6 px-4">
      {/* App-style shell header (match App.jsx main UI) */}
      <div className="bg-white border-b">
        <div className="pt-3 mb-4 px-4 max-w-screen-lg mx-auto">
          <div className="flex items-center justify-between">

            {/* Left: compact logo (click = go to Home) */}
            <a
              href="/"
              className="flex items-center focus:outline-none"
              aria-label="Go to Home"
            >
              <img
                src="/zabflow2.png"
                alt="ZabFlow AI™ Logo"
                className="h-12 md:h-14 w-auto select-none cursor-pointer"
              />
            </a>

            {/* Right: user info + credits + actions */}
            {user ? (
              <div className="text-right pr-2">
                <div>
                  Logged in as: <b>{user.email}</b>

                  <div className="text-gray-700">
                    <b>{user?.uses_custom_workflow ? "Custom Workflow" : "Generic Workflow"}</b>
                  </div>

                  <span>
                    Credits Remaining:{" "}
                    <b>
                      🪙{(user.credits ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </b>
                  </span>
                </div>

                <button
                  onClick={logout}
                  className="underline mt-1"
                  style={{ color: "#00a7e1" }}
                  type="button"
                >
                  Logout
                </button>

                {/* Ensure credits always re-render by referencing creditsTick */}
                <span style={{ display: "none" }}>{creditsTick}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Navigation links (so users don’t get stuck) */}
        <div className="max-w-screen-lg mx-auto flex justify-start mb-4 gap-2 px-4 flex-wrap">
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/?tab=main">
            Translate
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/?tab=projectRequests">
            Custom Requests
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/?tab=aiRequests">
            A.I. Requests
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/help">
            Help
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/about">
            About Zab
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/settings">
            Settings
          </a>
        </div>
      </div>

      <div className="p-6 max-w-xl mx-auto space-y-4">
        <h2 className="text-2xl font-bold">Payment Complete!</h2>

      {/* Status + controls */}
      <div className="text-sm text-gray-700">
        {provider === "paypal" && orderId && (
          <div className="mb-1">
            <span className="font-semibold">Provider:</span> PayPal
            <br />
            <span className="font-semibold">Order ID:</span> {orderId}
          </div>
        )}
        {statusMsg && <div className="mt-1">{statusMsg}</div>}
      </div>

      {provider === "paypal" && !done && (
        <button
          onClick={capturePaypal}
          disabled={capturing}
          className={`text-white font-bold px-4 py-2 rounded w-full ${capturing ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
          style={{ backgroundColor: "#94c83d" }}
        >
          {capturing ? "Finalizing…" : "Finalize PayPal Payment"}
        </button>
      )}

      <div className="flex flex-col gap-2">
        <a href="/" className="underline" style={{ color: "#00a7e1" }}>
          Return to ZabFlow AI™
        </a>
        <a href="/billing" className="underline" style={{ color: "#00a7e1" }}>
          Return to Buy Credits
        </a>
      </div>
    </div>
    </div>
  );
}

function ProjectPaymentSuccess() {
  const { jwt } = useAuth();
  const [msg, setMsg] = React.useState("Finalizing payment...");
  const [error, setError] = React.useState("");
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("provider");
    const token = params.get("token") || "";
    const sessionId = params.get("session_id") || "";
    const orderId = params.get("orderId") || params.get("order_id") || "";

    const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

    const finish = async () => {
      setError("");
      try {
        if (provider === "paypal") {
          if (!orderId || !token) throw new Error("Missing orderId or token");
          await axios.post(
            `${API_BASE}/billing/project/paypal/capture`,
            { order_id: orderId, approval_token: token },
            { headers }
          );
          setMsg("Payment received. Thank you!");
          setDone(true);
          return;
        }

        // Stripe payments are finalized via webhook. Poll the quote status briefly.
        if (!token) {
          setMsg("Payment received. Thank you!");
          setDone(true);
          return;
        }

        const started = Date.now();
        while (Date.now() - started < 20000) {
          try {
            const res = await axios.get(`${API_BASE}/project-approval/${token}`);
            const st = String(res?.data?.status || "");
            if (st === "Sent to Ops") {
              break;
            }
          } catch {
            // ignore transient
          }
          await new Promise((r) => setTimeout(r, 1500));
        }
        setMsg("Payment received. Thank you!");
        setDone(true);
      } catch (e) {
        console.error("Project payment finalize failed:", e);
        setError(e?.response?.data?.detail || "Failed to finalize payment.");
        setMsg("Payment received, but we could not confirm it yet. Please contact support if this persists.");
        setDone(true);
      }
    };

    finish();
    // eslint-disable-next-line
  }, [jwt]);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const backHref = token ? `/?quote=${encodeURIComponent(token)}#project-requests` : "/#project-requests";

  return (
    <div className="p-6">
      <div className="text-2xl font-semibold mb-3">Payment Status</div>
      <div className="text-gray-800">{msg}</div>
      {error ? <div className="mt-3 text-sm text-red-600">{error}</div> : null}
      {done ? (
        <div className="mt-5">
          <a
            href={backHref}
            className="inline-block px-4 py-2 rounded text-white font-semibold"
            style={{ backgroundColor: "#94c83d" }}
          >
            Back to Project Requests
          </a>
        </div>
      ) : null}
    </div>
  );
}


function BillingCancel() {
  return <PurchaseCredits billingStatus="cancel" />;
}

function TranslationUI({ homeResetNonce, onOpenAiRequestsForConvert }) {
  const navigate = useNavigate();
  // --- Auth (must be before any use of `user`/`jwt` to avoid TDZ) ---
  const { jwt, user, setUserAndTick, creditsTick } = useAuth();

  const [files, setFiles] = useState([]);
  const [sourceLang, setSourceLang] = useState("English");
  const [targetLangs, setTargetLangs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [currentItemName, setCurrentItemName] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const [customRequestSubmitted, setCustomRequestSubmitted] = useState(false);
  const [lastEstimate, setLastEstimate] = useState(null); // for showing cost/download after files cleared
  const [lastTaskId, setLastTaskId] = useState(null); // for download after translation
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const [analyzingPct, setAnalyzingPct] = useState(0);
  const [showAnalyzing, setShowAnalyzing] = useState(false);
  const analyzeTimerRef = useRef(null);
  const [requirements, setRequirements] = useState("");
  const [translationError, setTranslationError] = useState("");
  const [xliffPrompts, setXliffPrompts] = useState([]); // XLIFF array of file analysis
  const [xliffChoices, setXliffChoices] = useState({}); // XLIFF filename -> user choice
  const [xliffApplyAll, setXliffApplyAll] = useState({}); // XLIFF status_code -> user choice
  const [xliffPromptIndex, setXliffPromptIndex] = useState(0); //for XLIFF files
  const [okXliffPairs, setOkXliffPairs] = useState([]); //for XLIFF files
  const [langCodeMap, setLangCodeMap] = useState({});
  const [langNameToCode, setLangNameToCode] = useState({});
  
  // File type restriction (Custom Workflow)
  const [allowedExts, setAllowedExts] = useState(null);     // null => unrestricted; array => allowed exts only
  const [allowedTypeLabels, setAllowedTypeLabels] = useState(null); // for hint text
  const [lastRejectedExts, setLastRejectedExts] = useState([]); //Rejected file types for custom workflow


  // Display allowed file types in one normalized, alphabetical list everywhere in the UI
  const allowedTypeLabelsDisplay = React.useMemo(() => {
    const fallback = [
      "DOCX",
      "HTML",
      "IDML",
      "JSON",
      "PDF",
      "PPTX",
      "SDLXLIFF",
      "TXT",
      "XLF/XLIFF",
      "XLSX",
      "XML",
      "YML/YAML",
    ];

    const rawLabels = (allowedTypeLabels && allowedTypeLabels.length > 0)
      ? [...allowedTypeLabels]
      : fallback;

    const normalized = rawLabels.map((label) =>
      label === "PDF (limited support)" ? "PDF" : label
    );

    return Array.from(new Set(normalized)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [allowedTypeLabels]);

  const [workflowPEMT, setWorkflowPEMT] = useState(null);           // e.g., "full", "limited", "none"
  const [workflowFormatting, setWorkflowFormatting] = useState(null); // e.g., "enabled", "disabled", "none"
  
  const [skippedFiles, setSkippedFiles] = useState([]);
  
  // Language pairs from custom workflow (if enabled for this user)
  const [workflowPairs, setWorkflowPairs] = useState(null);

  // === Pair selection (Custom Workflow UI) ===
  const isCustomMode = !!(user && user.uses_custom_workflow);
  const [selectedPairs, setSelectedPairs] = useState([]); // [{source, target}]

  const [pairMixError, setPairMixError] = useState("");
  const [genericPairError, setGenericPairError] = useState("");
  const [srcInput, setSrcInput] = useState("");

  // Prevent mixing sources (until backend supports multi-source batching)
  useEffect(() => {
    if (!isCustomMode || !selectedPairs || selectedPairs.length === 0) {
      setPairMixError("");
      return;
    }

    const src = selectedPairs[0].source;
    const mixed = selectedPairs.some(p => p.source !== src);

    if (mixed) {
      const keep = selectedPairs.filter(p => p.source === src);
      setPairMixError("Only language pairs with the same source language can be selected.");
      setSelectedPairs(keep);
      return;
    }

    // If the user has at least one pair selected and sources are consistent, keep any prior warning visible
    // until it auto-clears (same pattern as genericPairError).
  }, [isCustomMode, selectedPairs]);

  useEffect(() => {
    if (!pairMixError) return;
    const t = setTimeout(() => setPairMixError(""), 4000);
    return () => clearTimeout(t);
  }, [pairMixError]);

useEffect(() => {
    if (!genericPairError) return;
    const t = setTimeout(() => setGenericPairError(""), 4000);
    return () => clearTimeout(t);
  }, [genericPairError]);

  // Create languageList from langCodeMap once loaded (names only, sorted)
  const languageList = React.useMemo(() =>
    Object.keys(langNameToCode).sort((a, b) => a.localeCompare(b)),
    [langNameToCode]
  );

  // Build pair options (Custom mode) → [{source, target, label}]
  const pairOptions = React.useMemo(() => {
    if (!isCustomMode || !workflowPairs || workflowPairs.length === 0) return [];
    const opts = workflowPairs
      .filter(p => p && typeof p.source === "string" && typeof p.target === "string")
      .map(p => ({
        source: p.source,
        target: p.target,
        label: `${p.source} \u2192 ${p.target}`, // "English → Spanish"
      }));
    // Sort by source, then target
    opts.sort((a, b) => {
      const s = a.source.localeCompare(b.source);
      return s !== 0 ? s : a.target.localeCompare(b.target);
    });
    return opts;
  }, [isCustomMode, workflowPairs]);

  // Inline toast for calm, non-blocking file-type errors
  const [toastMsg, setToastMsg] = useState("");
  const toastTimerRef = useRef(null);
  const showToast = (msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMsg(msg);
    toastTimerRef.current = setTimeout(() => setToastMsg(""), 5000);
  };

  // Estimate state
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState("");
  // Prevent stale /estimate responses from older file selections from overwriting the current estimate.
  const estimateRequestSeqRef = useRef(0);
  const estimateNonPdfRequestSeqRef = useRef(0);

  // Cancelled state when someone cancels a translation mid-stream
  const [wasCancelled, setWasCancelled] = useState(false);

  // --- Uploading Message
  const [showFilesUploading, setShowFilesUploading] = useState(false);

  const [downloadUrl, setDownloadUrl] = useState(null);

  // Separate estimate for non-PDF files (used to compute PEMT/Formatting while PDFs are present)
  const [estimateNonPdf, setEstimateNonPdf] = useState(null);

  // File helpers for PDF gating
  const hasPdf = React.useMemo(() => (files || []).some(f => ext(f.name) === ".pdf"), [files]);
  const pdfOnly = React.useMemo(() => (files || []).length > 0 && (files || []).every(f => ext(f.name) === ".pdf"), [files]);

  const refreshUser = async () => {
    if (downloadReady && jwt) {
      try {
        const res = await axios.get(
          `${API_BASE}/me`,
          { headers: { Authorization: `Bearer ${jwt}` } }
        );
        if (res.data && setUserAndTick) setUserAndTick(res.data);
      } catch (e) {}
    }
  };

  // ======= Toast Message Effects =======
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // ======= Download Ready =======
  useEffect(() => {
    if (downloadReady) {
      refreshUser();
    }
    // eslint-disable-next-line
  }, [downloadReady]);

  // ======= Fetch Language Codes =======
  useEffect(() => {
    async function fetchLangCodes() {
      const resp = await fetch(`${API_BASE}/lang_codes`);
      const data = await resp.json();
      setLangCodeMap(data.code_to_name || {});
      setLangNameToCode(data.name_to_code || {});
    }
    fetchLangCodes();
  }, []);

  // ======= Fetch Workflow Language Pairs (if custom workflow enabled) =======
  useEffect(() => {
    // If not logged in or custom workflow not enabled, fall back to global language list
    if (!jwt || !user || !user.uses_custom_workflow) {
      setWorkflowPairs(null);
      return;
    }

    const fetchPairs = async () => {
      try {
        const res = await axios.get(`${API_BASE}/workflow/lang_pairs`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        const pairs = res.data && Array.isArray(res.data.pairs) ? res.data.pairs : [];
        // Only store non-empty arrays; null = "no restriction"
        setWorkflowPairs(pairs.length > 0 ? pairs : null);
      } catch (err) {
        // Fail open if anything goes wrong; user keeps full language list
        setWorkflowPairs(null);
      }
    };

    fetchPairs();
  }, [jwt, user]);


  // ======= Snap sourceLang to a valid workflow source (avoid weird default) =======
  useEffect(() => {
    // Only apply when custom workflow is active
    if (!user || !user.uses_custom_workflow) return;
    if (!workflowPairs || workflowPairs.length === 0) return;

    // Build the allowed source set from workflowPairs
    const allowedSourceSet = new Set();
    workflowPairs.forEach((pair) => {
      const src = pair && typeof pair.source === "string" ? pair.source : null;
      const tgt = pair && typeof pair.target === "string" ? pair.target : null;
      if (src && tgt) {
        allowedSourceSet.add(src);
      }
    });

    // Intersect with the global language list to get the actual UI options
    const allowedSources = languageList.filter((name) => allowedSourceSet.has(name));
    if (allowedSources.length === 0) return;

    // If the current sourceLang is not in the allowed list, snap to the first allowed
    if (!sourceLang || !allowedSources.includes(sourceLang)) {
      const firstAllowed = allowedSources[0];
      if (firstAllowed && firstAllowed !== sourceLang) {
        setSourceLang(firstAllowed);
        setTargetLangs([]);      // clear targets since the source changed
        setEstimate(null);       // force re-estimation with the new source
        setEstimateError("");
        setTranslationError("");
      }
    }
  }, [user, workflowPairs, languageList, sourceLang]);


  // ======= Auto-select when exactly one allowed pair =======
  useEffect(() => {
    if (!user || !user.uses_custom_workflow) return;
    if (!workflowPairs || workflowPairs.length === 0) return;

    // Build map: source -> set(targets)
    const map = {};
    for (const p of workflowPairs) {
      if (!p || typeof p.source !== "string" || typeof p.target !== "string") continue;
      if (!map[p.source]) map[p.source] = new Set();
      map[p.source].add(p.target);
    }

    const sources = Object.keys(map);
    if (sources.length !== 1) return; // only apply when there's exactly one source
    const onlySource = sources[0];
    const targets = Array.from(map[onlySource] || []);
    if (targets.length !== 1) return; // only apply when that source has exactly one target
    const onlyTarget = targets[0];

    // Set defaults if not already selected
    let changed = false;
    if (sourceLang !== onlySource) {
      setSourceLang(onlySource);
      changed = true;
    }
    if (!(targetLangs.length === 1 && targetLangs[0] === onlyTarget)) {
      setTargetLangs([onlyTarget]);
      changed = true;
    }

    if (changed) {
      setEstimate(null);
      setEstimateError("");
      setTranslationError("");
    }
  }, [user, workflowPairs, sourceLang, targetLangs]);

  // ======= Prune targetLangs to allowed set (when Custom mode is active) =======
  useEffect(() => {
    if (!user || !user.uses_custom_workflow) return;
    if (!workflowPairs || workflowPairs.length === 0) return;
    if (!sourceLang) return;

    // Build allowed targets for the current source
    const allowedTargets = new Set(
      workflowPairs
        .filter(p => p && p.source === sourceLang && typeof p.target === "string")
        .map(p => p.target)
    );

    // If any chosen target is not allowed, prune it
    if (targetLangs.some(t => !allowedTargets.has(t))) {
      const next = targetLangs.filter(t => allowedTargets.has(t));
      if (next.length !== targetLangs.length) {
        setTargetLangs(next);
        setEstimate(null);        // re-estimate if selection changed
        setEstimateError("");
        setTranslationError("");
      }
    }
  }, [user, workflowPairs, sourceLang, targetLangs]);

  // ======= Fetch Workflow File Types (if custom workflow enabled) =======
  useEffect(() => {
    // If not logged in or custom workflow not enabled, allow all
    if (!jwt || !user || !user.uses_custom_workflow) {
      setAllowedExts(null);
      setAllowedTypeLabels(null);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/workflow/draft/me`, {
          headers: { Authorization: `Bearer ${jwt}` },
          withCredentials: true,
        });
        const draft = res?.data || {};

        // Capture PEMT + Formatting mode (used for “Additional Costs” UI on Home)
        const pemtRaw = (draft && typeof draft.pemt === "string") ? draft.pemt : null;
        const fmtRaw  = (draft && typeof draft.formatting === "string") ? draft.formatting : null;

        // Normalize to lowercase keywords; accept many spellings, but keep original labels for display later
        const pemtNorm = (pemtRaw || "").toLowerCase().trim();
        const fmtNorm  = (fmtRaw  || "").toLowerCase().trim();

        // Store the original strings; the UI will check truthiness/keywords
        setWorkflowPEMT(pemtRaw || null);
        setWorkflowFormatting(fmtRaw || null);

        let labels = [];
        try {
          labels = Array.isArray(draft.file_types_json)
            ? draft.file_types_json
            : (draft.file_types_json ? JSON.parse(draft.file_types_json) : []);
        } catch {
          labels = [];
        }

        // If none selected, treat as unrestricted
        if (!labels || labels.length === 0) {
          setAllowedExts(null);
          setAllowedTypeLabels(null);
          return;
        }

        // Normalize labels and build extensions set
        const extSet = new Set();

        // Helper: add any explicit extensions embedded in a label string
        const addExtsFromString = (s) => {
          if (typeof s !== "string") return;
          const extsInText = s.toLowerCase().match(/(\.[a-z0-9]+)\b/g);
          if (extsInText) {
            extsInText.forEach(e => extSet.add(e));
          }
        };

        labels.forEach(lbl => {
          const raw = String(lbl || "");
          addExtsFromString(raw);

          // Normalize label to a canonical key for the map
          let key = raw.toUpperCase().trim();
          // Strip parentheticals like "DOCX (.docx)"
          key = key.replace(/\s*\(.*?\)\s*/g, "");
          // Collapse multiple spaces
          key = key.replace(/\s+/g, " ");

          // Common synonyms → canonical keys
          if (key === "WORD" || key === "MS WORD" || key === "DOC" || key === "DOC/DOCX") key = "DOCX";
          if (key === "EXCEL" || key === "XLS") key = "XLSX";
          if (key === "POWERPOINT" || key === "PPT") key = "PPTX";
          if (key === "TEXT" || key === "PLAIN TEXT") key = "TXT";
          if (key === "YAML" || key === "YML") key = "YML/YAML";
          if (key === "XLF" || key === "XLIFF") key = "XLF/XLIFF";
          if (key === "SDL XLIFF" || key === "SDL-XLIFF" || key === "SDLXLF") key = "SDLXLIFF";

          const list = FILE_TYPE_TO_EXTS[key];
          if (Array.isArray(list)) {
            list.forEach(e => extSet.add(e));
          }
        });

        const finalExts = Array.from(extSet);
        if (finalExts.length === 0) {
          // If nothing recognized, fail open (allow all) to avoid blocking uploads,
          // but you can switch this to a fail-closed if desired.
          setAllowedExts(null);
          setAllowedTypeLabels(null);
          return;
        }

        setAllowedExts(finalExts);
        setAllowedTypeLabels(labels);
      } catch (_) {
        // Fail open on any error
        setAllowedExts(null);
        setAllowedTypeLabels(null);
      }
    })();
  }, [jwt, user]);


  // ======= Universal Language Display =======
  function displayLang(codeOrName) {
    if (!codeOrName) return "";
    // Try code→name (most cases, XLIFF headers etc.)
    if (langCodeMap[codeOrName]) return langCodeMap[codeOrName]; // code → pretty name
    // Try fallback: case-insensitive match
    const lc = codeOrName.toLowerCase().replace(/_/g, "-");
    for (const [code, name] of Object.entries(langCodeMap)) {
      if (code.toLowerCase().replace(/_/g, "-") === lc) return name;
    }
    return codeOrName; // fallback, show raw code if unmapped
  }

  // === Unified blinking dots for all "..." messages ===
  const [dotCount, setDotCount] = useState(1);
  useEffect(() => {
    let interval;
    if (uploading || showFilesUploading || estimating) {
      interval = setInterval(() => {
        setDotCount(prev => (prev % 3) + 1);
      }, 600);
    } else {
      setDotCount(1);
    }
    return () => clearInterval(interval);
  }, [uploading, showFilesUploading, estimating]);

  // ======= FILE/ESTIMATE LOGIC =======

  // Dropzone logic (additive, never replaces native picker) — enforces limits at selection time
  const onDrop = (acceptedFiles, fileRejections) => {
    setTranslationError("");
    setEstimateError("");
    setDownloadUrl(null);
    setLastRejectedExts([]);

    // Capture files that react-dropzone rejected (based on `accept`)
    if (Array.isArray(fileRejections) && fileRejections.length) {
      const bad = fileRejections.map(r => r.file).filter(Boolean);
      if (bad.length) {
        const badExts = Array.from(new Set(bad.map(b => ext(b.name)).filter(Boolean)));
        if (badExts.length) setLastRejectedExts(badExts);

        // Custom workflow: rejected types mean "not part of workflow"; Generic workflow: "not supported"
        if (allowedExts && allowedExts.length > 0) {
          const labels = Array.from(new Set(badExts.map(e => EXT_TO_LABEL[e] || e.toUpperCase())));
          const msg = labels.length === 1
            ? `${labels[0]} files are not part of your custom workflow.`
            : `${labels.join(", ")} files are not part of your custom workflow.`;
          showToast(msg);
          setEstimateError(msg);
        } else {
          const msg = badExts.length === 1
            ? `${badExts[0]} files are not supported.`
            : `${badExts.join(", ")} files are not supported.`;
          showToast(msg);
          setEstimateError(msg);
        }
      }
    }


    // --- File type restriction (if set) ---
    if (allowedExts && allowedExts.length > 0) {
      const bad = (acceptedFiles || []).filter(f => !allowedExts.includes(ext(f.name)));
      if (bad.length) {
        const badExts = Array.from(new Set(bad.map(b => ext(b.name))));
        setLastRejectedExts(badExts);
        const labels = Array.from(new Set(badExts.map(e => EXT_TO_LABEL[e] || e.toUpperCase())));
        const msg = labels.length === 1
          ? `${labels[0]} files are not part of your custom workflow.`
          : `${labels.join(", ")} files are not part of your custom workflow.`;
        showToast(msg);
        setEstimateError(msg);
      }
      acceptedFiles = (acceptedFiles || []).filter(f => allowedExts.includes(ext(f.name)));
    }

    setFiles(prevFiles => {
      // 0) If Dropzone rejected everything (no acceptedFiles), show custom-workflow message and stop
      if (Array.isArray(fileRejections) && fileRejections.length && (!acceptedFiles || acceptedFiles.length === 0)) {
        const bad = fileRejections.map(r => r.file).filter(Boolean);
        const badExts = Array.from(new Set(bad.map(b => ext(b.name)).filter(Boolean)));

        if (allowedExts && allowedExts.length > 0) {
          const labels = Array.from(new Set(badExts.map(e => EXT_TO_LABEL[e] || e.toUpperCase())));
          const msg = labels.length === 1
            ? `${labels[0]} files are not part of your custom workflow.`
            : `${labels.join(", ")} files are not part of your custom workflow.`;
          showToast(msg);
          setEstimateError(msg);
        } else {
          const msg = badExts.length === 1
            ? `${badExts[0]} files are not supported.`
            : `${badExts.join(", ")} files are not supported.`;
          showToast(msg);
          setEstimateError(msg);
        }

        if (fileInputRef.current) fileInputRef.current.value = null;
        return prevFiles; // add none
      }

      // 1) If selection itself exceeds max, reject entirely
      if ((acceptedFiles || []).length > LIMITS.maxFilesPerBatch) {
        const msg = `Too many files. Max ${LIMITS.maxFilesPerBatch}.`;
        alert(msg);
        setEstimating(false);
        setUploading(false);
        setEstimateError(msg);               // <-- make the reason visible
        if (fileInputRef.current) fileInputRef.current.value = null;
        return prevFiles;                     // add none
      }

      // 2) Partition incoming: invalid (> per-file cap) vs valid
      const oversize = [];
      const validIncoming = [];
      for (const f of acceptedFiles || []) {
        const cap = perFileCapBytes(f.name);
        if (f.size > cap) {
          oversize.push(f.name);
        } else {
          validIncoming.push(f);
        }
      }
      if (oversize.length) {
        const msg =
          `The following file(s) exceed the per-file limit and were not added:\n\n` +
          oversize.join("\n");
        alert(msg);
        setEstimateError(msg); // surface the full list to the user

        if (validIncoming.length === 0) {
          setEstimating(false);
          setUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = null;
          return prevFiles;
        }
      }

      // 3) Merge valid incoming by unique name
      const mergedMap = new Map(prevFiles.map(f => [f.name, f]));
      validIncoming.forEach(f => mergedMap.set(f.name, f));
      const next = Array.from(mergedMap.values());

      // 4) If merged count exceeds max, reject entire selection
      if (next.length > LIMITS.maxFilesPerBatch) {
        const msg = `Too many files. Max ${LIMITS.maxFilesPerBatch}.`;
        alert(msg);
        setEstimating(false);
        setUploading(false);
        setEstimateError(msg);               // <-- keep reason visible
        if (fileInputRef.current) fileInputRef.current.value = null;
        return prevFiles;                     // add none
      }

      // 5) If nothing remains after filtering, show a custom-workflow message instead of the generic one
      if ((acceptedFiles || []).length > 0 && next.length === 0 && allowedExts && allowedExts.length > 0) {
        const extsToLabel = (lastRejectedExts.length > 0
          ? lastRejectedExts
          : (acceptedFiles || []).map(f => ext(f.name)));
        const labels = Array.from(new Set(extsToLabel.map(e => EXT_TO_LABEL[e] || e.toUpperCase())));
        const msg = labels.length === 1
          ? `${labels[0]} files are not part of your custom workflow.`
          : `${labels.join(", ")} files are not part of your custom workflow.`;
        showToast(msg);
        setEstimateError(msg);
        if (fileInputRef.current) fileInputRef.current.value = null;
        return prevFiles; // add none
      }

      // 6) Validate batch (per-file + total MB)
      const verdict = validateSelectedFiles(next);
      if (!verdict.ok) {
        alert(verdict.message);
        setEstimating(false);
        setUploading(false);
        setEstimateError(verdict.message);   // <-- keep reason visible
        if (fileInputRef.current) fileInputRef.current.value = null;
        return prevFiles;                     // add none
      }

      // 6) Accept selection — now it’s safe to clear stale estimate and mark estimating
      if (next.length > 0) {
        setEstimate(null);                   // clear only when we actually accept files
        setEstimating(true);                 // <-- ensures the section shows analyzing while /estimate runs
        setDownloadReady(false);
        setLastEstimate(null);
        setLastTaskId(null);
        setWasCancelled(false);
      }
      if (fileInputRef.current) fileInputRef.current.value = null;
      return next;
    });
  };

  const dropzoneAccept = React.useMemo(() => {
    return allowedExts ? buildAcceptFromExts(allowedExts) : DEFAULT_DROPZONE_ACCEPT;
  }, [allowedExts]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: dropzoneAccept,
  });


  // Native file input handler — enforces limits at selection time
  const handleFileChange = (e) => {
    setTranslationError("");
    setEstimateError("");
    setDownloadUrl(null); // Clear stale download link on new upload

    const incoming = Array.from(e?.target?.files || []);

    // --- File type restriction (if set) ---
    let filteredIncoming = incoming;
    if (allowedExts && allowedExts.length > 0) {
      const bad = incoming.filter(f => !allowedExts.includes(ext(f.name)));
      if (bad.length) {
        setLastRejectedExts(Array.from(new Set(bad.map(b => ext(b.name)))));
        showToast(
          "Some files were not added due to file type restrictions: " +
          bad.map(b => b.name).join(", ")
        );
      }
      filteredIncoming = incoming.filter(f => allowedExts.includes(ext(f.name)));
    }

    // Generic workflow: enforce supported file types even if the OS file picker shows "All Files (*.*)"
    if (!allowedExts || allowedExts.length === 0) {
      const bad = incoming.filter(f => !DEFAULT_ACCEPTED_EXTS.includes(ext(f.name)));
      if (bad.length) {
        const badExts = Array.from(new Set(bad.map(b => ext(b.name)).filter(Boolean)));
        if (badExts.length) setLastRejectedExts(badExts);
        const msg = badExts.length === 1
          ? `${badExts[0]} files are not supported.`
          : `${badExts.join(", ")} files are not supported.`;
        showToast(msg);
        setEstimateError(msg);
      }
      filteredIncoming = incoming.filter(f => DEFAULT_ACCEPTED_EXTS.includes(ext(f.name)));
    }


    setFiles(prevFiles => {
      // 1) If selection itself exceeds max, reject entirely
      if (filteredIncoming.length > LIMITS.maxFilesPerBatch) {
        const msg = `Too many files. Max ${LIMITS.maxFilesPerBatch}.`;
        alert(msg);
        setEstimating(false);
        setUploading(false);
        setEstimateError(msg);               // <-- keep reason visible
        if (fileInputRef.current) fileInputRef.current.value = null;
        if (e?.target) e.target.value = "";  // reset input
        return prevFiles;                     // add none
      }

      // 2) Partition incoming: invalid (> per-file cap) vs valid
      const oversize = [];
      const validIncoming = [];
      for (const f of filteredIncoming) {
        const cap = perFileCapBytes(f.name);
        if (f.size > cap) {
          oversize.push(f.name);
        } else {
          validIncoming.push(f);
        }
      }
      if (oversize.length) {
        const msg =
          `The following file(s) exceed the per-file limit and were not added:\n\n` +
          oversize.join("\n");
        alert(msg);
        setEstimateError(msg);               // <-- surface the full list to the user

        if (validIncoming.length === 0) {
          setEstimating(false);
          setUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = null;
          if (e?.target) e.target.value = "";
          return prevFiles;
        }
      }

      // 3) Merge valid incoming by unique name
      const mergedMap = new Map(prevFiles.map(f => [f.name, f]));
      validIncoming.forEach(f => mergedMap.set(f.name, f));
      const next = Array.from(mergedMap.values());

      // 4) If merged count exceeds max, reject entire selection
      if (next.length > LIMITS.maxFilesPerBatch) {
        const msg = `Too many files. Max ${LIMITS.maxFilesPerBatch}.`;
        alert(msg);
        setEstimating(false);
        setUploading(false);
        setEstimateError(msg);
        setEstimate && setEstimate(null);    // in case it was showing from a prior selection
        setDownloadReady(false);
        setLastEstimate(null);
        setLastTaskId(null);
        setWasCancelled(false);
        if (fileInputRef.current) fileInputRef.current.value = null;
        if (e?.target) e.target.value = "";  // reset input
        return prevFiles;                     // add none
      }

      // 5) If nothing remains after filtering, show a targeted message (custom workflow vs generic)
      if (incoming.length > 0 && next.length === 0) {
        if (allowedExts && allowedExts.length > 0) {
          const extsToLabel = (lastRejectedExts.length > 0
            ? lastRejectedExts
            : incoming.map(f => ext(f.name)));
          const labels = Array.from(new Set(extsToLabel.map(e => EXT_TO_LABEL[e] || e.toUpperCase())));
          const msg = labels.length === 1
            ? `${labels[0]} files are not part of your custom workflow.`
            : `${labels.join(", ")} files are not part of your custom workflow.`;
          showToast(msg);
          setEstimateError(msg);
        } else {
          const badExts = Array.from(new Set(incoming.map(f => ext(f.name)).filter(e => e && !DEFAULT_ACCEPTED_EXTS.includes(e))));
          if (badExts.length) setLastRejectedExts(badExts);
          const msg = badExts.length === 1
            ? `${badExts[0]} files are not supported.`
            : `${badExts.join(", ")} files are not supported.`;
          showToast(msg);
          setEstimateError(msg);
        }
        if (fileInputRef.current) fileInputRef.current.value = null;
        if (e?.target) e.target.value = "";  // reset input
        return prevFiles; // add none
      }
// 6) Validate batch (per-file + total MB)
      const verdict = validateSelectedFiles(next);
      if (!verdict.ok) {
        alert(verdict.message);
        setEstimating(false);
        setUploading(false);
        setEstimateError(verdict.message);   // <-- keep reason visible
        if (fileInputRef.current) fileInputRef.current.value = null;
        if (e?.target) e.target.value = "";  // reset input
        return prevFiles;                     // add none
      }

      // 7) Accept selection — now it’s safe to clear stale estimate and mark estimating
      if (next.length > 0) {
        setEstimate(null);                   // clear only when we actually accept files
        setEstimating(true);                 // <-- ensures the section shows analyzing while /estimate runs
        setDownloadReady(false);
        setLastEstimate(null);
        setLastTaskId(null);
        setWasCancelled(false);
      }
      if (fileInputRef.current) fileInputRef.current.value = null;
      if (e?.target) e.target.value = "";    // reset input
      return next;
    });
  };

  // Remove a single file
  const handleRemoveFile = (index) => {
    // Clear stale estimate/warning immediately; the useEffect below will recalculate for the remaining files.
    setEstimate(null);
    setEstimateNonPdf(null);
    setEstimateError("");
    setEstimating(true);
    setDownloadReady(false);
    setLastEstimate(null);
    setLastTaskId(null);
    setWasCancelled(false);
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  
// Reset the request state (used by the Thank You screen "Start a New Request" button)
const resetRequestState = () => {
  setTranslationError("");
  setEstimateError("");
  setDownloadUrl(null);
  setToastMsg("");
  setCustomRequestSubmitted(false);

  // Clear selection + estimate state
  setFiles([]);
  setTargetLangs([]);
  setSelectedPairs([]);
  setPairMixError("");
  setGenericPairError("");
  setSrcInput("");
  setEstimate(null);
  setEstimateNonPdf(null);

  // Clear instructions + progress/download state
  setRequirements("");
  setUploading(false);
  setProgress(0);
  setProgressMessage("");
  setCurrentItemName(null);
  setTaskId(null);
  setIsCancelled(false);
  setWasCancelled(false);
  setDownloadReady(false);
  setLastEstimate(null);
  setLastTaskId(null);
  setShowFilesUploading(false);

  // Clear any XLIFF modal state
  setXliffChoices({});
  setXliffPrompts([]);
  setOkXliffPairs([]);
  setXliffPromptIndex(0);
  setXliffApplyAll({});
  setSkippedFiles([]);

  if (pollRef.current) clearInterval(pollRef.current);
  if (analyzeTimerRef.current) clearTimeout(analyzeTimerRef.current);
  if (fileInputRef.current) fileInputRef.current.value = null;
};

// If the user is on the Thank You screen, allow the Home/logo click to reset the request
useEffect(() => {
  if (!homeResetNonce) return;
  resetRequestState();
  // eslint-disable-next-line
}, [homeResetNonce]);


// Clear all files
  const handleClearFiles = () => {
    resetRequestState();
  };

  // Map pair selection -> effective (single) source and targets (keep backend happy)
  const effectiveSource = React.useMemo(() => {
    if (!isCustomMode) return sourceLang;
    if (!selectedPairs || selectedPairs.length === 0) return sourceLang;
    // Enforce one-source-at-a-time: take the first pair's source
    return selectedPairs[0].source || sourceLang;
  }, [isCustomMode, selectedPairs, sourceLang]);

  const effectiveTargets = React.useMemo(() => {
    if (!isCustomMode) return targetLangs;
    if (!selectedPairs || selectedPairs.length === 0) return targetLangs;
    const chosen = selectedPairs
      .filter(p => p && p.source === effectiveSource)
      .map(p => p.target);
    return Array.from(new Set(chosen));
  }, [isCustomMode, selectedPairs, effectiveSource, targetLangs]);

  // Estimating Progress
  useEffect(() => {
    if (!files.length || !(effectiveTargets && effectiveTargets.length)) {
      estimateRequestSeqRef.current += 1;
      estimateNonPdfRequestSeqRef.current += 1;
      setEstimating(false);
      setEstimate(null);
      setEstimateNonPdf(null);
      setProgress(0);
      setProgressMessage("");
      setCurrentItemName(null);
      setIsCancelled(false);
      return;
    }

    // Final safety-net: Reject files that violate Custom Workflow file type restrictions
    if (allowedExts && allowedExts.length > 0) {
      const bad = files.filter(f => !allowedExts.includes(ext(f.name)));
      if (bad.length > 0) {
        const msg =
          "One or more files are not allowed by your Custom Workflow: " +
          bad.map(b => b.name).join(", ");
        showToast(msg);
        setEstimating(false);
        setEstimate(null);
        setEstimateError(msg);
        return;
      }
    }

    setEstimating(true);
    setAnalyzingPct(0);
    setShowAnalyzing(false);
    clearTimeout(analyzeTimerRef?.current);

    const estimateRequestId = estimateRequestSeqRef.current + 1;
    estimateRequestSeqRef.current = estimateRequestId;

    let fakeProgressActive = true;
    const totalFakeTime = 12000;
    const fakeMaxPct = 95;
    analyzeTimerRef.current = setTimeout(() => {
      setShowAnalyzing(true);
      let pct = 0;
      const step = fakeMaxPct / (totalFakeTime / 80);
      function increment() {
        if (!fakeProgressActive) return;
        pct += step;
        if (pct > fakeMaxPct) pct = fakeMaxPct;
        setAnalyzingPct(Math.floor(pct));
        if (pct < fakeMaxPct) {
          analyzeTimerRef.current = setTimeout(increment, 80);
        }
      }
      increment();
    }, 2000);

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("source_lang", effectiveSource);
    effectiveTargets.forEach(lang => formData.append("target_langs", lang));

    // === Pass PEMT + Formatting to /estimate so backend can compute credits ===
    const pemtLabel = String(workflowPEMT || "").toLowerCase();
    const fmtLabel  = String(workflowFormatting || "").toLowerCase();

    // Treat "term_egregious" (limited review) as "limited"
    const isNone = (!pemtLabel || ["none","no","disabled","false"].includes(pemtLabel));
    const isLimited = (!isNone) && (
      pemtLabel.includes("limited") ||
      pemtLabel.includes("egregious") ||
      pemtLabel.includes("term")
    );
    const pemtMode = isNone ? "none" : (isLimited ? "limited" : "full");

    const formattingEnabled = !!(fmtLabel && !["none","no","disabled","false"].includes(fmtLabel));

    formData.append("pemt_mode", pemtMode);
    formData.append("formatting", formattingEnabled ? "true" : "false");

    axios.post(
      `${API_BASE}/estimate`,
      formData,
      { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
    )

      .then(res => {
        if (estimateRequestSeqRef.current !== estimateRequestId) return;
        fakeProgressActive = false;
        clearTimeout(analyzeTimerRef.current);
        setShowAnalyzing(false);
        setAnalyzingPct(100);
        setEstimate(res.data);
      })
      .catch(err => {
        if (estimateRequestSeqRef.current !== estimateRequestId) return;
        fakeProgressActive = false;
        clearTimeout(analyzeTimerRef.current);
        setShowAnalyzing(false);
        setEstimate({ error: "Estimation failed. Try again." });
      })
      .finally(() => {
        if (estimateRequestSeqRef.current === estimateRequestId) setEstimating(false);
      });
    // eslint-disable-next-line
  }, [files, effectiveSource, effectiveTargets, allowedExts]);

  // Re-estimate for non-PDF files when PDFs are present and a post-step is enabled
  useEffect(() => {
    // Derive post-step flags locally (mirror the logic used above)
    const pemtLabel = String(workflowPEMT || "").toLowerCase();
    const fmtLabel  = String(workflowFormatting || "").toLowerCase();
    const pemtOn = pemtLabel && !["none","no","disabled","false"].includes(pemtLabel);
    const fmtOn  = fmtLabel  && !["none","no","disabled","false"].includes(fmtLabel);

    if (!hasPdf || (!pemtOn && !fmtOn)) {
      estimateNonPdfRequestSeqRef.current += 1;
      setEstimateNonPdf(null);
      return;
    }

    const nonPdfFiles = (files || []).filter(f => ext(f.name) !== ".pdf");
    if (nonPdfFiles.length === 0) {
      estimateNonPdfRequestSeqRef.current += 1;
      setEstimateNonPdf(null);
      return;
    }

    const fd2 = new FormData();
    nonPdfFiles.forEach((file) => fd2.append("files", file));
    fd2.append("source_lang", effectiveSource);
    (effectiveTargets || []).forEach(lang => fd2.append("target_langs", lang));

    // Map PEMT mode once more (full vs limited vs none)
    const isLimited = pemtLabel.includes("limited") || pemtLabel.includes("egregious") || pemtLabel.includes("term");
    const pemtMode = !pemtOn ? "none" : (isLimited ? "limited" : "full");
    fd2.append("pemt_mode", pemtMode);
    fd2.append("formatting", fmtOn ? "true" : "false");

    const estimateNonPdfRequestId = estimateNonPdfRequestSeqRef.current + 1;
    estimateNonPdfRequestSeqRef.current = estimateNonPdfRequestId;

    axios.post(
      `${API_BASE}/estimate`,
      fd2,
      { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
    )
      .then(res2 => {
        if (estimateNonPdfRequestSeqRef.current !== estimateNonPdfRequestId) return;
        setEstimateNonPdf(res2.data);
      })
      .catch(() => {
        if (estimateNonPdfRequestSeqRef.current !== estimateNonPdfRequestId) return;
        setEstimateNonPdf(null);
      });
  // deps: whenever file list, langs, or post-step toggles change
  }, [files, hasPdf, sourceLang, targetLangs, workflowPEMT, workflowFormatting, jwt]);

  // === TRANSLATION LOGIC ===
  const handleSubmit = async () => {
    setTranslationError(""); // Always clear error before processing
    setEstimateError("");    // Always clear error before processing
    setXliffChoices({});
    setXliffPrompts([]);
    setOkXliffPairs([]);
    setXliffPromptIndex(0);

    // --- If selection became empty (likely due to file-type filtering), prefer custom-workflow message
    if (!files.length) {
      if (allowedExts && allowedExts.length > 0) {
        if (lastRejectedExts.length > 0) {
          const labels = Array.from(new Set(lastRejectedExts.map(e => EXT_TO_LABEL[e] || e.toUpperCase())));
          const msg = labels.length === 1
            ? `${labels[0]} files are not part of your custom workflow.`
            : `${labels.join(", ")} files are not part of your custom workflow.`;
          showToast(msg);
          setEstimateError(msg);
        } else {
          const msg = "The selected file type is not part of your custom workflow.";
          showToast(msg);
          setEstimateError(msg);
        }
      } else {
        const msg = (lastRejectedExts && lastRejectedExts.length > 0)
          ? "The file you selected is not a valid file type."
          : "Please select at least one file.";
        showToast(msg);
        setEstimateError(msg);
      }
      return;
    }

    // --- Upload limits preflight (count, per-file size, total batch)
    const preflight = validateSelectedFiles(files);
    if (!preflight.ok) {
      setEstimateError(preflight.message);
      alert(preflight.message);
      return;
    }

    if (!(effectiveTargets && effectiveTargets.length)) {
      setEstimateError("Please select at least one language pair.");
      return;
    }

    // Custom workflow: if PEMT or Formatting/QC is enabled, treat as a custom request.
    // The files are processed in the backend, but we do not show progress, do not email the user,
    // and do not provide a download link in the UI.
    const customOn = !!(user && user.uses_custom_workflow);
    const pemtLabel = String(workflowPEMT || "").toLowerCase();
    const fmtLabel  = String(workflowFormatting || "").toLowerCase();
    const pemtOffSet = new Set(["none","no","disabled","false"]);
    const pemtOn = customOn && pemtLabel && !pemtOffSet.has(pemtLabel);
    const fmtOn  = customOn && fmtLabel  && !pemtOffSet.has(fmtLabel);

    if (customOn && (pemtOn || fmtOn)) {
      const taskId = uuidv4();
      setTaskId(taskId);

      const formData = new FormData();
      formData.append("source_lang", effectiveSource);
      effectiveTargets.forEach(lang => formData.append("target_langs", lang));
      formData.append("task_id", taskId);
      files.forEach((file) => formData.append("files", file));
      formData.append("requirements", requirements || "");

      // If this is a custom request (Accept Estimate), send the estimate details for internal e-mail
      // Send the estimate details for internal e-mail
      try {
        formData.append("custom_request", "1");
        if (estimate) formData.append("estimate_json", JSON.stringify(estimate));
        // Prefer a numeric word-count if present in the estimate payload
        const wc =
          (estimate && (estimate.estimated_word_count ?? estimate.word_count ?? estimate.total_words ?? estimate.words)) ||
          null;
        if (wc !== null && wc !== undefined && wc !== "") {
          formData.append("estimated_word_count", String(wc));
        }
      } catch (e) {
        // ignore
      }
      formData.append("original_filenames", JSON.stringify(files.map(f => f.name)));
      formData.append("diagnostics_filename", files[0].name);

      try {
        await axios.post(
          `${API_BASE}/translate`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            }
          }
        );

        // Clear the request inputs like Cancel does (files + language pairs)
        setFiles([]);
        setTargetLangs([]);
        setSelectedPairs([]);
        setEstimate(null);
        setEstimateError("");
        setTranslationError("");
        setDownloadReady(false);
        setLastEstimate(null);
        setLastTaskId(null);
        setWasCancelled(false);

        if (fileInputRef.current) fileInputRef.current.value = null;

        setCustomRequestSubmitted(true);
        await refreshUser();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.detail) {
          alert(error.response.data.detail);
          setEstimateError(error.response.data.detail);
        } else {
          alert("Request failed. Please try again or select another file.");
          setEstimateError("Request failed. Please try again or select another file.");
        }
      }
      return;
    }

    setUploading(true);

    setIsCancelled(false);
    setProgress(0);
    setProgressMessage(`Starting translation of ${files.length} file${files.length !== 1 ? "s" : ""} to ${effectiveTargets.length} language${effectiveTargets.length !== 1 ? "s" : ""}...`);
    setCurrentItemName(null);
    setDownloadReady(false);

    const taskId = uuidv4();
    setTaskId(taskId);

    const formData = new FormData();
    formData.append("source_lang", effectiveSource);
    effectiveTargets.forEach(lang => formData.append("target_langs", lang));
    formData.append("task_id", taskId);
    files.forEach((file) => formData.append("files", file));
	formData.append("requirements", requirements || "");
	formData.append("original_filenames", JSON.stringify(files.map(f => f.name)));
	formData.append("diagnostics_filename", files[0].name);
	
    try {
      const response = await axios.post(
        `${API_BASE}/translate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
          }
        }
      );

      // XLIFF user prompt branch
      if (response.data && response.data.status === "needs_user_choice") {
        const allPrompts = response.data.files || [];
        const okPrompts = allPrompts.filter(p => p.status_code === "OK");
        const filteredPrompts = allPrompts.filter(p => p.status_code !== "OK");

        setOkXliffPairs(okPrompts); // <-- always update okXliffPairs with the latest green pairs
        setXliffPrompts(filteredPrompts);
        setXliffPromptIndex(0);
        setUploading(false); // Pause uploading until resolved
        return;
      }

      pollProgress(taskId, files);
      await refreshUser();
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.detail
          ? error.response.data.detail
          : "Translation failed. Please try again or select another file.";
      setProgressMessage("");
      setTranslationError(message);
      alert(message);
      setUploading(false);
    }
  };

  // === Download logic ===
  const pollProgress = async (taskId, files) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/progress/${taskId}`,
          { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
        );
        const { progress, message, status } = res.data;

        let displayMessage = message || "";

        // If the backend sends a generic message like "Translating..." or "Translating... 25%", inject the filename for display.
        // Otherwise, trust the backend's message as-is.

        let showFilename = /^Translating(?:\.{0,3})(?:\s*\d+%.*)?$/i.test(displayMessage.trim());

        if (showFilename) {
          // For PDFs and all files: use actual user file name and language
          const nameToShow = files.length === 1 ? files[0].name : (currentItemName || "file");
          displayMessage = `Translating ${nameToShow} to ${targetLangs[0] || "target language"}... ${progress}%`;
        }

        // Normalize to a placeholder "..." so rendering can animate it with dotCount
        displayMessage = displayMessage
          .replace(/\u2026/g, "...")   // convert true ellipsis to "..."
          .replace(/\.{2,}/g, "...");  // collapse any 2+ dots to exactly "..."

        // --- Handle error status ---
        if (status === "error") {
          setUploading(false);
          setProgress(0);
          setProgressMessage(""); // Clear message
          setEstimateError(""); // Clear message
          // Only show a real error, not a progress message
		  const userError = (!message || /^Translating\b/i.test(message.trim()))
            ? "This file could not be processed. Please try a different file."
            : message;
          setTranslationError(userError);
          clearInterval(pollRef.current);
          setDownloadReady(false);
          setDownloadUrl(null);
          setFiles([]);
          return;
        }

        setProgress(progress);
        setProgressMessage(
          (status !== "completed" && status !== "cancelled")
            ? `[${files.length} file${files.length !== 1 ? "s" : ""} × ${effectiveTargets.length} language${effectiveTargets.length !== 1 ? "s" : ""}]<br/>${displayMessage}`
            : displayMessage
        );
  
        if (status === "completed") {
          // 1. Show 'Files uploading...' and begin polling download endpoint.
          setShowFilesUploading(true);
          setProgressMessage(`Files uploading...`);
          setUploading(false);

          // 2. Poll for ZIP file availability before showing download button
          const checkZip = async () => {
            try {
              const res = await axios.get(
                `${API_BASE}/download/` + taskId,
                { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
              );
              if (res.data && res.data.url) {
                setDownloadUrl(res.data.url);
                setShowFilesUploading(false);
                setDownloadReady(true);
                // standardized success message handled in download section
                setProgress(100);
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = null;
                setLastEstimate(estimate);
                setLastTaskId(taskId);
                setFiles([]);
                setTargetLangs([]);
                setRequirements("");
                await refreshUser();
                return;
              }
            } catch (e) {
              // 404 means not ready, keep waiting
            }
            setTimeout(checkZip, 2000);
          };
          checkZip();
          clearInterval(pollRef.current);
          return;
        }
      } catch (e) {
        // Optionally: Handle poll error
      }
    }, 1000);
  };

  // === Cancel logic ===
  const handleCancel = async () => {
    setTranslationError("");
    setCustomRequestSubmitted(false);
    if (!taskId) return;
    setIsCancelled(true);
    setWasCancelled(true);
    setUploading(false);
    clearInterval(pollRef.current);

    // Clear the download URL
    setDownloadUrl(null);
    
	// Reset all XLIFF modal/prompt state
    setXliffPrompts([]);
    setXliffChoices({});
    setXliffApplyAll({});
    setXliffPromptIndex(0);
    setSkippedFiles([]);
		
    try {
      await axios.post(
        `${API_BASE}/cancel/${taskId}`,
        {},
        { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
      );
      setProgressMessage("Translation canceled.");
    } catch (e) {
      setProgressMessage("Cancel request failed.");
    }

    if (fileInputRef.current) fileInputRef.current.value = null;
    setFiles([]); // CLEAR selected files
    setTargetLangs([]); // CLEAR language chips
    setSelectedPairs([]); // CLEAR custom workflow language pair chips
    setPairMixError("");
    setGenericPairError("");
    setRequirements(""); // CLEAR additional instructions
	
	setTimeout(() => {
      setFiles([]);
      setTargetLangs([]);
      setSelectedPairs([]);
      setPairMixError("");
      setGenericPairError("");
      setEstimate(null);
      setRequirements("");
    }, 10);
	
  };

  useEffect(() => {
    if (files.length > 0) {
      const dt = new DataTransfer();
      files.forEach(f => dt.items.add(f));
    }
    // eslint-disable-next-line
  }, [sourceLang, targetLangs]);


// ============== XLIFF Prompt Modal ==============
const handleXliffPromptChoice = async (choice, isCancel = false) => {
  const currPrompt = xliffPrompts[xliffPromptIndex];
  const pairKey = `${currPrompt.filename}|${currPrompt.target_lang}`;
  const code = currPrompt.status_code;

  // For language-switch prompts, Cancel should stop processing entirely.
  if (isCancel && ["SOURCE_LOCKED", "HEADER_SWITCH_PROMPT"].includes(code)) {
    await handleCancel();
    return;
  }

  let nextChoices = { ...xliffChoices, [pairKey]: choice };

  // --- Apply to All logic (per-file, not global) ---
  if (xliffApplyAll[code]) {
    const currentFile = currPrompt.filename;
    xliffPrompts.forEach((prompt, idx) => {
      const pk = `${prompt.filename}|${prompt.target_lang}`;
      if (
        prompt.status_code === code &&
        prompt.filename === currentFile &&    // Only same file!
        nextChoices[pk] === undefined &&
        idx > xliffPromptIndex               // Only subsequent prompts for this file
      ) {
        nextChoices[pk] = choice;
      }
    });
  }

  // If skipping, mark this file+lang as skipped
  if (choice === "skip") {
    setSkippedFiles(prev => [...prev, pairKey]);
  }

  // Move to next prompt (skip already-answered ones)
  const prevFile = xliffPrompts[xliffPromptIndex]?.filename;
  let nextIdx = xliffPromptIndex + 1;
  while (
    nextIdx < xliffPrompts.length &&
    nextChoices[`${xliffPrompts[nextIdx].filename}|${xliffPrompts[nextIdx].target_lang}`] !== undefined
  ) {
    nextIdx++;
  }
  const nextFile = xliffPrompts[nextIdx]?.filename;
  if (nextFile && nextFile !== prevFile) {
    setXliffApplyAll({});
  }

  setXliffChoices(nextChoices);
  setXliffPromptIndex(nextIdx);

  // If all are answered, proceed
  if (nextIdx >= xliffPrompts.length) {
    setXliffPromptIndex(0);
    setXliffPrompts([]);
    setXliffApplyAll({});

    // Build a mapping of file -> langs to include (not skipped)
    const fileLangsMap = {};
    xliffPrompts.forEach(prompt => {
      const thisKey = `${prompt.filename}|${prompt.target_lang}`;
      if (nextChoices[thisKey] !== "skip") {
        if (!fileLangsMap[prompt.filename]) fileLangsMap[prompt.filename] = [];
        if (!fileLangsMap[prompt.filename].includes(prompt.target_lang)) {
          fileLangsMap[prompt.filename].push(prompt.target_lang);
        }
      }
    });

    // Keeps green lit files in output
    if (okXliffPairs && okXliffPairs.length > 0) {
      okXliffPairs.forEach(prompt => {
        if (!fileLangsMap[prompt.filename]) fileLangsMap[prompt.filename] = [];
        if (!fileLangsMap[prompt.filename].includes(prompt.target_lang)) {
          fileLangsMap[prompt.filename].push(prompt.target_lang);
        }
      });
    }

    // Ensure non-XLIFF files are always included with all targetLangs
    const xliffFilenames = new Set(xliffPrompts.map(p => p.filename));
    const allFileLangsMap = { ...fileLangsMap };

    // Add non-XLIFF files to fileLangsMap if not already present
    files.forEach(f => {
      if (!allFileLangsMap[f.name]) {
        allFileLangsMap[f.name] = [...targetLangs];
      }
    });

    // Always include all files for translation
    const filteredFiles = [...files];

    // Compute unique set of languages to process
    const filteredTargetLangs = Array.from(
      new Set(Object.values(allFileLangsMap).flat())
    );

    // If all are skipped (i.e., user cancelled everything), handle cancel
    const noLangsSelected = Object.values(allFileLangsMap).flat().length === 0;
    if (filteredFiles.length === 0 || filteredTargetLangs.length === 0 || noLangsSelected) {
      setUploading(false);
      setTranslationError("No files or languages selected for translation.");
      if (isCancel || Object.values(nextChoices).every(v => v === "skip")) {
        handleCancel();
      } else {
        setTimeout(() => {
          setFiles([]);
          setTargetLangs([]);
          setEstimate(null);
          setRequirements("");
        }, 10);
      }
      return;
    }
    await recalculateEstimateForFiltered(filteredFiles, filteredTargetLangs);
    setUploading(true);
    submitTranslationWithChoices(nextChoices, filteredFiles, filteredTargetLangs);
  }
};

// ====== Recalculate and Show Updated Costs for XLIFF File/Language Cancellations ======
async function recalculateEstimateForFiltered(filesToSend, langsToSend) {
  if (!filesToSend.length || !langsToSend.length) {
    setEstimate(null);
    return;
  }
  setEstimating(true);
  setEstimateError("");
  
  const formData = new FormData();
  filesToSend.forEach((file) => formData.append("files", file));
  (langsToSend || effectiveTargets).forEach(lang => formData.append("target_langs", lang));
  formData.append("source_lang", effectiveSource);

  // === Pass PEMT + Formatting to /estimate here as well ===
  const pemtLabel = String(workflowPEMT || "").toLowerCase();
  const fmtLabel  = String(workflowFormatting || "").toLowerCase();

  // Treat “term_egregious” (limited review) as "limited"
  const isNone    = (!pemtLabel || ["none","no","disabled","false"].includes(pemtLabel));
  const isLimited = (!isNone) && (
    pemtLabel.includes("limited") ||
    pemtLabel.includes("egregious") ||
    pemtLabel.includes("term")
  );
  const pemtMode = isNone ? "none" : (isLimited ? "limited" : "full");

  const formattingEnabled = !!(fmtLabel && !["none","no","disabled","false"].includes(fmtLabel));
  formData.append("pemt_mode", pemtMode);
  formData.append("formatting", formattingEnabled ? "true" : "false");

  try {
    const res = await axios.post(
      `${API_BASE}/estimate`,
      formData,
      { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
    );

    setEstimate(res.data);
  } catch (e) {
    setEstimate({ error: "Estimation failed. Try again." });
  } finally {
    setEstimating(false);
  }
}

// ====== XLIFF Languages Leave/Overwrite Choices ======
async function submitTranslationWithChoices(choices, filteredFiles = files, filteredTargetLangs = targetLangs) {
  setUploading(true);

  // --- Build file->allowedLangs mapping from XLIFF prompts (per-file/per-lang) ---
  const fileLangsMap = {};

  // Gather all XLIFF-prompted files:
  const xliffFilenames = new Set(xliffPrompts.map(p => p.filename));

  // Build XLIFF mapping: Only add languages NOT skipped
  xliffPrompts.forEach(prompt => {
    const thisKey = `${prompt.filename}|${prompt.target_lang}`;
    if (choices[thisKey] !== "skip") {
      if (!fileLangsMap[prompt.filename]) fileLangsMap[prompt.filename] = [];
      if (!fileLangsMap[prompt.filename].includes(prompt.target_lang)) {
        fileLangsMap[prompt.filename].push(prompt.target_lang);
      }
    }
  });

  // === Always include green/OK pairs ===
  if (okXliffPairs && okXliffPairs.length > 0) {
    okXliffPairs.forEach(prompt => {
      if (!fileLangsMap[prompt.filename]) fileLangsMap[prompt.filename] = [];
      if (!fileLangsMap[prompt.filename].includes(prompt.target_lang)) {
        fileLangsMap[prompt.filename].push(prompt.target_lang);
      }
    });
  }

  // For any non-XLIFF file, include *all* requested targetLangs
  filteredFiles.forEach((file) => {
    if (!xliffFilenames.has(file.name)) {
      fileLangsMap[file.name] = [...filteredTargetLangs];
    }
  });

  const formData = new FormData();
  formData.append("source_lang", effectiveSource);
  (filteredTargetLangs && filteredTargetLangs.length ? filteredTargetLangs : effectiveTargets)
    .forEach(lang => formData.append("target_langs", lang));
  formData.append("task_id", taskId);
  filteredFiles.forEach((file) => formData.append("files", file));
  formData.append("requirements", requirements || "");
  formData.append("original_filenames", JSON.stringify(filteredFiles.map(f => f.name)));
  formData.append("diagnostics_filename", filteredFiles[0]?.name || "");
  formData.append("xliff_choices", JSON.stringify(choices));
  formData.append("file_lang_map", JSON.stringify(fileLangsMap));  // <-- Only append ONCE, with both XLIFF & non-XLIFFs

  try {
    await axios.post(
      `${API_BASE}/translate`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        }
      }
    );
    pollProgress(taskId, filteredFiles);
    await refreshUser();
  } catch (error) {
    setUploading(false);
    let msg = "Translation failed. Please try again or contact support.";
    if (error.response && error.response.data && error.response.data.detail) {
      msg = error.response.data.detail;
    }
    setTranslationError(msg);
  }
}


// ====== XLIFF Modal JSX ======
const xliffPromptModal = (
  xliffPrompts.length > 0 && xliffPromptIndex < xliffPrompts.length && (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white p-6 rounded shadow-xl max-w-lg w-full">
        <h2 className="text-xl font-semibold mb-2">
          XLIFF File: {xliffPrompts[xliffPromptIndex].filename}
          <br />
          Target Language: {displayLang(xliffPrompts[xliffPromptIndex].target_lang)}
        </h2>

        <div className="mb-4">
          {(() => {
            const prompt = xliffPrompts[xliffPromptIndex];
            const code = prompt.status_code;
            const userSrc = displayLang(sourceLang);
            const userTgt = displayLang(xliffPrompts[xliffPromptIndex].target_lang);
            const fileSrc = displayLang(prompt.header_source_lang);
            const fileTgt = displayLang(prompt.header_target_lang);

            // Only show source change if source differs (case-insensitive, dash/underscore-insensitive)
            const srcChanged = !codeEquiv(sourceLang, prompt.header_source_lang, langNameToCode, langCodeMap);
            const tgtChanged = !codeEquiv(prompt.target_lang, prompt.header_target_lang, langNameToCode, langCodeMap);

            // === NEW: SOURCE_LOCKED HANDLING ===
            if (code === "SOURCE_LOCKED") {
              return (
                `The source language is currently set as '${fileSrc}' and the target language is set as '${fileTgt}'. The source language cannot be changed. Do you want to change the target language to '${userTgt}'? Changing the target language will overwrite any current '${fileTgt}' translations.`
              );
            }

            if (code === "HEADER_SWITCH_PROMPT") {
              // Both differ
              if (srcChanged && tgtChanged) {
                let msg = `The source language is currently set as '${fileSrc}' and the target language is set as '${fileTgt}'. `;
                msg += `Do you want to change the source language to '${userSrc}' and the target language to '${userTgt}'? `;
                if (prompt.num_targets_filled > 0) {
                  msg += `Changing the target language will overwrite any current '${fileTgt}' translations.`;
                }
                return msg;
              }
              // Only target differs
              if (!srcChanged && tgtChanged) {
                let msg = `The target language is set as '${fileTgt}'. `;
                msg += `Do you want to change the target language to '${userTgt}'? `;
                if (prompt.num_targets_filled > 0) {
                  msg += `Changing the target language will overwrite any current '${fileTgt}' translations.`;
                }
                return msg;
              }
              // Only source differs
              if (srcChanged && !tgtChanged) {
                return `The source language is currently set as '${fileSrc}'. Do you want to change the source language to '${userSrc}'?`;
              }
            }
            if (code === "OVERWRITE_PROMPT" || code === "PARTIAL_TARGETS_PROMPT") {
              return `The XLIFF file already contains ${prompt.num_targets_filled} existing '${fileTgt}' translations. Do you want to overwrite them with new '${userTgt}' translations, or leave the current translations?`;
            }
            if (code === "INVALID_XLIFF") {
              return `This file is not a valid XLIFF file.`;
            }
            return prompt.message;
          })()}
        </div>

        <div className="flex flex-col gap-2 mt-2">
          {(() => {
            const code = xliffPrompts[xliffPromptIndex].status_code;
            const options = [];
            const buttonStyle = (val) => {
              if (val === "skip") {
                return { borderColor: "#dc2626", color: "#dc2626" };
              }
              if (val === "overwrite" || val === "switch_header") {
                return { borderColor: "#00a7e1", color: "#00a7e1" };
              }
              if (val === "fill_missing") {
                return { borderColor: "#94c83d", color: "#5a7f22" };
              }
              return { borderColor: "#00a7e1", color: "#00a7e1" };
            };

            // === Selection Options for XLIFF ===
            if (code === "SOURCE_LOCKED") {
              options.push({ label: "Change Target Language", value: "overwrite" });
              options.push({ label: "Cancel", value: "skip" });
            } else if (code === "HEADER_SWITCH_PROMPT") {
              options.push({ label: "Change Target Language", value: "switch_header" });
              options.push({ label: "Cancel", value: "skip" });
			} else if (code === "OVERWRITE_PROMPT" || code === "PARTIAL_TARGETS_PROMPT") {
              options.push({ label: "Leave Current Translations", value: "fill_missing" });
              options.push({ label: "Overwrite Current Translations", value: "overwrite" });
            } else if (code === "INVALID_XLIFF") {
              options.push({ label: "Skip this file", value: "skip" });
            }
            if (options.length === 0) {
              options.push({ label: "Proceed", value: "proceed" });
            }
            return options.map(opt => (
              <button
                key={opt.value}
                className={`bg-white border-2 px-4 py-2 rounded font-semibold mb-2 hover:opacity-90`}
                style={buttonStyle(opt.value)}
				onClick={() => {
                  if (opt.value === "skip") {
                    handleXliffPromptChoice("skip", true);  // pass isCancel flag
                  } else {
                    handleXliffPromptChoice(opt.value, false);
                  }
                }}
              >
                {opt.label}
              </button>
            ));
          })()}
          {/* Apply to All */}
          {["OVERWRITE_PROMPT", "PARTIAL_TARGETS_PROMPT", "HEADER_SWITCH_PROMPT"].includes(xliffPrompts[xliffPromptIndex].status_code) && (
            <div className="mt-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  onChange={e => setXliffApplyAll({ ...xliffApplyAll, [xliffPrompts[xliffPromptIndex].status_code]: e.target.checked })}
                  checked={!!xliffApplyAll[xliffPrompts[xliffPromptIndex].status_code]}
                />
                <span className="ml-2">Apply to all files with this prompt</span>
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  )
);

if (xliffPrompts.length > 0 && xliffPromptIndex < xliffPrompts.length) {
  return xliffPromptModal;
}


// ======================================
// === Translation UI Main ==============
// ======================================

// Compute language options, respecting custom workflow language pairs if present
let sourceOptions = languageList;
let targetOptions = languageList.filter((lang) => lang !== sourceLang);

if (user && user.uses_custom_workflow && workflowPairs && workflowPairs.length > 0) {
  // Build allowed sources and per-source targets from workflow pairs
  const allowedSourceSet = new Set();
  const targetsBySource = {};

  workflowPairs.forEach((pair) => {
    const src = pair.source;
    const tgt = pair.target;
    if (typeof src === "string" && typeof tgt === "string") {
      allowedSourceSet.add(src);
      if (!targetsBySource[src]) {
        targetsBySource[src] = [];
      }
      if (!targetsBySource[src].includes(tgt)) {
        targetsBySource[src].push(tgt);
      }
    }
  });

  // Restrict sources to those that exist both in the pairs and in the global list
  sourceOptions = languageList.filter((name) => allowedSourceSet.has(name));

  // Restrict targets to those allowed for the currently selected source
  if (sourceLang && targetsBySource[sourceLang]) {
    targetOptions = targetsBySource[sourceLang].filter((name) => name !== sourceLang);
  } else {
    // If current source has no configured targets, show an empty list
    targetOptions = [];
  }
  
  // Put "English" at the top of the Source list (if present)
  if (Array.isArray(sourceOptions) && sourceOptions.length) {
    const hasEnglish = sourceOptions.includes("English");
    if (hasEnglish) {
      sourceOptions = ["English", ...sourceOptions.filter(n => n !== "English")];
    }
  }

}

// Put "English" at the top for generic workflow as well
if (!(user && user.uses_custom_workflow)) {
  if (Array.isArray(sourceOptions) && sourceOptions.length) {
    const hasEnglish = sourceOptions.includes("English");
    if (hasEnglish) {
      sourceOptions = ["English", ...sourceOptions.filter(n => n !== "English")];
    }
  }
}




  // Determine whether the top "Costs for X Languages" box should show
  const showAdditionalCosts = (() => {
    if (!files || files.length === 0) return false;               // must have at least one file
    const customOn = !!(user && user.uses_custom_workflow);
    const pemtLabel = String(workflowPEMT || "").toLowerCase();
    const fmtLabel  = String(workflowFormatting || "").toLowerCase();
    const pemtOn = customOn && pemtLabel && !["none","no","disabled","false"].includes(pemtLabel);
    const fmtOn  = customOn && fmtLabel  && !["none","no","disabled","false"].includes(fmtLabel);
    if (!pemtOn && !fmtOn) return false;                          // only show if either option is on
    if (!estimate || estimate.error) return false;                 // wait until estimate is available
    return true;
  })();

  const hidePreGifOptions = uploading || showFilesUploading || downloadReady;


if (customRequestSubmitted) {
    return (
      <div className="p-6 max-w-xl mx-auto space-y-6 font-sans">
        <div className="bg-white border rounded p-6 text-center">
          <div className="text-xl font-bold mb-2">Thank you!</div>
          <div className="text-base">
            Your translation request is being processed. You should receive a final cost for approval within 24 hours.
          </div>
        </div>
        <button
          onClick={() => resetRequestState()}
          className="text-white font-bold px-4 py-2 rounded w-full hover:opacity-90"
          style={{ backgroundColor: "#94c83d" }}
        >
          Start a New Request
        </button>
      </div>
    );
  }

return (
  <div data-testid="translate-page" className="p-6 max-w-xl mx-auto space-y-6 font-sans">

    {!hidePreGifOptions && (
      <>

    {/* Language Selection */}
    {isCustomMode ? (
      // === Custom Workflow: Pair-based selection ===
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block font-medium mb-2">Language Pairs</label>
          
		  <Autocomplete
 		   multiple
		    disableClearable
		    clearIcon={null}
 		   options={pairOptions}
 		   getOptionLabel={(opt) => opt.label}
		    value={selectedPairs}
		    onChange={(event, newValue) => {
               const next = newValue || [];

               // Block mixed-source selection and show the warning below the chips
               if (selectedPairs && selectedPairs.length > 0 && next.length > selectedPairs.length) {
                 const baseSrc = selectedPairs[0].source;
                 const hasMixed = next.some(p => p && p.source !== baseSrc);
                 if (hasMixed) {
                   setPairMixError("Only language pairs with the same source language can be selected.");
                   return;
                 }
               }

               setPairMixError("");
               setSelectedPairs(next);

               // Snap "classic" controls to effective values so legacy logic remains stable
               const src = (next && next[0] && next[0].source) || sourceLang;
               setSourceLang(src);
               const tgts = Array.from(new Set(next.filter(p => p.source === src).map(p => p.target)));
               setTargetLangs(tgts);
               setEstimate(null);
               setEstimateError("");
               setTranslationError("");
             }}

 		   renderInput={(params) => (
 		     <TextField
  		      {...params}
  		      label="Select Language Pairs"
  		      variant="outlined"
  		      placeholder={params.inputProps.value === "" && selectedPairs.length === 0 ? "Language Pairs" : ""}
  		    />
 		   )}
 		   sx={{ marginBottom: 2 }}
 		   filterSelectedOptions
 		   renderTags={() => null}
		  />

          {/* Pair chips */}
          {selectedPairs.length > 0 && (
            <>
              <div className="flex flex-wrap gap-3 mt-2">
                {selectedPairs.map((p) => {
                  const key = `${p.source}→${p.target}`;
                  return (
                    <span
                      key={key}
                      className="bg-blue-100 border border-black px-3 py-1 rounded-full text-sm flex items-center"
                      style={{ color: "black" }}
                    >
                      {p.source} &rarr; {p.target}
                      <button
                        className="ml-2 text-red-500 font-bold text-base hover:opacity-80"
                        onClick={() => {
                          const next = selectedPairs.filter(x => !(x.source === p.source && x.target === p.target));
                          setSelectedPairs(next);
                          const src = (next[0] && next[0].source) || sourceLang;
                          setSourceLang(src);
                          const tgts = Array.from(new Set(next.filter(pp => pp.source === src).map(pp => pp.target)));
                          setTargetLangs(tgts);
                          setEstimate(null);
                        }}
                        aria-label={`Remove ${p.source} → ${p.target}`}
                        type="button"
                        style={{ fontSize: "1rem" }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
              {pairMixError && (
                <div className="mt-2 rounded border px-3 py-2 text-sm bg-yellow-50 border-yellow-300 text-yellow-800">
                  {pairMixError}
                </div>
              )}
            </>
          )}
        </div>
      </div>

    ) : (
      // === Generic Workflow: original source/target dropdowns (+ pair-like chips derived from current source) ===
      <>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-2">Source Language</label>

            <Autocomplete
              data-testid="translate-source-language"
              disableClearable
              options={sourceOptions}
              value={sourceLang}
              inputValue={srcInput}
              onInputChange={(_, val) => setSrcInput(val)}
              onChange={(event, newValue) => {
                if (newValue && newValue !== sourceLang && targetLangs.length > 0) {
                  setGenericPairError("Only language pairs with the same source language can be selected.");
                  setSrcInput(""); // clear the typed filter so the list repopulates immediately
                  return;
                }
                setGenericPairError("");
                setSrcInput("");
                setSourceLang(newValue);
                setTargetLangs([]);
                setEstimate(null);
                setEstimateError("");
                setTranslationError("");
              }}
              onOpen={() => setSrcInput("")}
			  renderInput={(params) => (
                <TextField
                  {...params}
                  label="Source Language"
                  variant="outlined"
                  placeholder={
                    params.inputProps.value === "" && !sourceLang ? "Source Language" : ""
                  }
                />
              )}
              sx={{ marginBottom: 2 }}
            />
          </div>
          
		  <div>
            <label className="block font-medium mb-2">Target Languages</label>
            <Autocomplete
              data-testid="translate-target-language"
              multiple
              disableClearable
              options={targetOptions}
              value={targetLangs}
              onChange={(event, newValue) => {
                setGenericPairError("");
                setTargetLangs(newValue);
                setEstimate(null);
                setEstimateError("");
                setTranslationError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Target Language(s)"
                  variant="outlined"
                  placeholder={
                    params.inputProps.value === ""
                      ? (targetLangs.length === 0
                          ? "Select Target Language(s)"
                          : "Add another language...")
                      : ""
                  }
                />
              )}
              sx={{ marginBottom: 2 }}
              filterSelectedOptions
              renderTags={() => null}
            />

            {/* In Generic mode, show pair-like chips using current source */}
            <div className="flex flex-wrap gap-3 mt-2">
              {[...targetLangs].sort().map((lang) => (
                <span
                  key={`pair-${sourceLang}-${lang}`}
                  className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-700 ring-1 ring-blue-200"
                >
                  {sourceLang} &rarr; {lang}
                  <button
                    type="button"
                    className="ml-2 text-red-500 font-bold text-base hover:opacity-80"
                    onClick={() => {
                      setGenericPairError("");
                      setTargetLangs((prev) => prev.filter((l) => l !== lang));
                    }}
                    aria-label={`Remove ${sourceLang} to ${lang}`}
                    title={`Remove ${sourceLang} \u2192 ${lang}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Full-width inline warning inside the grid */}
          {genericPairError && (
            <div className="col-span-2 mt-1 rounded border px-3 py-2 text-sm bg-yellow-50 border-yellow-300 text-yellow-800">
              {genericPairError}
            </div>
          )}
        </div>
      </>
    )}




    {/* Language selection validation */}
    {((isCustomMode && (!selectedPairs || selectedPairs.length === 0)) || (!isCustomMode && (!targetLangs || targetLangs.length === 0))) && (
      <div data-testid="translate-no-languages" className="text-red-600 mb-2 font-semibold">No languages selected.</div>
    )}
    {/* Dropzone: supports drag & drop or click (additive) */}
    <div>
      <label className="block font-medium mb-2">Upload Files</label>
      <div
        {...getRootProps()}
        data-testid="translate-upload-dropzone"
        className={`border-2 border-dashed rounded-lg p-6 mb-2 text-center cursor-pointer transition 
            ${isDragActive ? "border-blue-600 bg-blue-50" : "border-gray-400 bg-gray-50 hover:border-blue-600"}
            `}
        style={{ minHeight: 120 }}
      >
        <input
          {...getInputProps()}
          data-testid="translate-file-input"
          style={{ display: "none" }}
        />
        <div className="text-gray-500">
          {isDragActive
            ? "Drop files here…"
            : "Drag and drop files here, or click to choose"}
          <br />
          <span className="text-xs">
            <>
              Allowed:{" "}
              {allowedTypeLabelsDisplay.map((label, idx) => (
                <React.Fragment key={`${label}-${idx}`}>
                  {idx > 0 ? ", " : ""}
                  <span className="whitespace-nowrap">{label}</span>
                </React.Fragment>
              ))}
            </>
          </span>
        </div>
      </div>
      {/* Inline toast for file-type restrictions (auto hides in 5s) */}
      {toastMsg && (
        <div data-testid="translate-upload-toast" className="mt-2 mb-2 rounded border px-3 py-2 text-sm bg-yellow-50 border-yellow-300 text-yellow-800">
          {toastMsg}
        </div>
      )}

      {/* Displays upload limits */}
      <p className="text-sm text-gray-500 mt-2">
        Up to <strong>10 files</strong>, max <strong>25 MB</strong> per file (format-dependent), total <strong>150 MB</strong>.
      </p>

      {/* File input/button for "Choose Files" */}
      <div className="mt-3">
        <label
          className="text-white px-3 py-2 rounded mt-4 w-full hover:opacity-90"
          style={{ backgroundColor: "#94c83d" }}
        >
          Choose Files
          <input
            data-testid="translate-choose-files-input"
            type="file"
            multiple
            accept={allowedExts ? allowedExts.join(",") : ".docx,.html,.idml,.json,.pdf,.pptx,.sdlxliff,.txt,.xlf,.xliff,.xlsx,.xml,.yaml,.yml"}
            onChange={handleFileChange}
            ref={fileInputRef}
            className="hidden"
          />
        </label>

        {/* Tiny helper subtitle mirroring the Allowed list */}
        <div className="text-xs text-gray-500 mt-2">
          {`Allowed: ${allowedTypeLabelsDisplay.join(", ")}` }
        </div>
      </div>

      {/* File list with remove/clear */}
      <div className="mt-2">
        {files.length > 0 ? (
          <div data-testid="translate-selected-files" className="mb-2 border rounded p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium" style={{ color: "#00a7e1" }}>
                Selected Files ({files.length})
              </div>
                <button
                  data-testid="translate-clear-files"
                  type="button"
                  className="text-sm underline"
                  style={{ color: "#00a7e1" }}
                  onClick={handleClearFiles}
                >
                  Clear All Files
                </button>
            </div>

            <ul className="space-y-1">
              {files.map((file, i) => (
                <li data-testid="translate-selected-file" key={file.name} className="flex items-center justify-between px-2 py-1 bg-white rounded border">
                  <span className="truncate font-medium">{file.name}</span>
                  <button
                    data-testid="translate-file-remove"
                    type="button"
                    className="text-red-600 hover:underline ml-2 text-sm"
                    onClick={() => handleRemoveFile(i)}
                    aria-label={`Remove ${file.name}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            {files.some((file) => ext(file.name) === ".pdf") && (
              <div className="mt-2 text-xs font-semibold" style={{ color: "#dc2626" }}>
                * PDF file support may be limited
              </div>
            )}
          </div>
        ) : (
          <div data-testid="translate-no-files" className="text-red-600 mb-2 font-semibold">No files selected.</div>
        )}
      </div>
    </div>

    {/* Requirements Box */}
      <div className="mt-4">
        <label className="block font-medium">
          <span className="flex items-center gap-2">
            Additional Instructions
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              style={{ backgroundColor: "#94c83d33", color: "#5a7f22" }}
              aria-label="Beta"
              title="Beta"
            >
              Beta
            </span>
          </span>
        </label>
        <div className="text-xs text-gray-500 mb-2">
          (i.e., "Don't translate text between curly brackets in TXT file")
        </div>
        <textarea
          data-testid="translate-additional-instructions"
          className="w-full border rounded p-2 text-base"
          rows={3}
          placeholder='Add any translation requirements (optional)'
          value={requirements}
          onChange={e => setRequirements(e.target.value)}
        />
      </div>

      {/* Costs for X Files into X Languages (renders only when estimate is ready and PEMT and/or Formatting is enabled) */}
      {showAdditionalCosts && (() => {
        const pemtLabel = String(workflowPEMT || "").toLowerCase();
        const fmtLabel  = String(workflowFormatting || "").toLowerCase();
        const pemtOn = pemtLabel && !["none","no","disabled","false"].includes(pemtLabel);
        const fmtOn  = fmtLabel  && !["none","no","disabled","false"].includes(fmtLabel);

        let pemtRowTitle = "Estimated Full Human Post-Edit Review";
        if (pemtLabel.includes("limited") || pemtLabel.includes("egregious") || pemtLabel.includes("term")) {
          pemtRowTitle = "Estimated Limited Review";
        }

        const wc  = (typeof estimate?.word_count_total === "number") ? estimate.word_count_total : null;

        // Prefer non-PDF estimate for PEMT/Formatting if available (so PDFs don't contribute to those lines)
        const pcr = (typeof estimateNonPdf?.pemt_credits === "number")
          ? estimateNonPdf.pemt_credits
          : ((typeof estimate?.pemt_credits === "number") ? estimate.pemt_credits : 0);

        const fcr = (typeof estimateNonPdf?.formatting_credits === "number")
          ? estimateNonPdf.formatting_credits
          : ((typeof estimate?.formatting_credits === "number") ? estimate.formatting_credits : 0);

        const fmtNum = (n) =>
          (typeof n === "number"
            ? n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
            : "—");
        const fmtDollars = (n) =>
          (typeof n === "number"
            ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "—");
        const fmtRange = (n) => {
          const totalNum = Number(n || 0);
          if (!Number.isFinite(totalNum) || totalNum <= 0) return "—";
          const low = totalNum * 0.90;
          const high = totalNum * 1.25;
          return `${fmtDollars(low)} - ${fmtDollars(high)}`;
        };

  const formatDocxArtifactWarning = (files) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    if (!list.length) return "";
    if (list.length === 1) {
      return `${list[0]} appears to contain PDF-to-Word conversion artifacts. Post-formatting time may be underestimated.`;
    }
    if (list.length === 2) {
      return `${list[0]} and ${list[1]} appear to contain PDF-to-Word conversion artifacts. Post-formatting time may be underestimated.`;
    }
    const head = list.slice(0, -1).join(", ");
    const tail = list[list.length - 1];
    return `${head}, and ${tail} appear to contain PDF-to-Word conversion artifacts. Post-formatting time may be underestimated.`;
  };


        const aiCost = Number(estimate?.cost || 0);

        // Total includes AI + any enabled post steps (using non-PDF estimate where present)
        const totalAll = Number(estimate?.grand_total_estimate || (aiCost + (pemtOn ? pcr : 0) + (fmtOn ? fcr : 0)) || 0);

        // Header: "Costs for X Files into Y Languages"
        const numFiles = Math.max(1, files.length);
        const numLangs = Math.max(1, targetLangs.length);
        const headerText =
          `Estimated Costs for ${numFiles} File${numFiles !== 1 ? "s" : ""} into ${numLangs} Language${numLangs !== 1 ? "s" : ""}`;

        // If mixed PDFs + others and any post step is on, show a red advisory listing the PDF file(s)
        const pdfNames = (files || []).filter(f => ext(f.name) === ".pdf").map(f => f.name);

        return (
          <div className="mt-4 border rounded p-3 bg-gray-50">
            {/* PDF files are now included in post-AI translation review and formatting estimates. */}
            <div className="font-semibold mb-1">
              Estimated cost is {fmtRange(totalAll)}
            </div>
            {hasPdf ? (
              <div className="text-sm mb-1">
                Please note that scanned pages or embedded images in PDF files may not be included in this estimate.
              </div>
            ) : null}
            {estimate?.docx_artifact_warning ? (
              <div className="text-sm mb-1" style={{ color: "#dc2626" }}>
                {formatDocxArtifactWarning(estimate?.docx_artifact_warning_files)}
              </div>
            ) : null}

            {/* Warning when formatting is on but no review is selected (moved below range) */}
            {fmtOn && !pemtOn && (
              <div className="text-sm font-semibold mt-1" style={{ color: "#dc2626" }}>
                Post-translation formatting is <span className="underline">not</span> recommended if post AI-translation review is not done as the AI translation may cause formatting issues that cannot be repaired.
              </div>
            )}


            <div className="text-sm font-semibold mt-1" style={{ color: "#dc2626" }}>
              You will not be charged by accepting the estimate. You will be able to approve/reject final costs before the translation is started.
            </div>
            <div className="text-xs mt-1 text-gray-700">
              To remove different costs, please modify your custom workflow.
            </div>

            {/* Word count */}
            {typeof wc === "number" && (
              <div className="text-xs mt-1 text-gray-600">
              </div>
            )}
          </div>
        );
      })()}


	  {/* Cost/Ready/Download UI */}
      {estimateError && (
        <div data-testid="translate-estimate-error" className="text-red-600 text-center">{estimateError}</div>
      )}
      {!showAdditionalCosts && ((estimate && !estimate.error) || (lastEstimate && downloadReady) || wasCancelled) && (
        <>
          <div data-testid="translate-cost-summary" className="text-sm bg-gray-100 p-2 rounded my-2 text-center">
            {wasCancelled ? (
              <b>Translation canceled.</b>
            ) : downloadReady && lastEstimate ? (
              <></>
            ) : (
              (() => {
                const cost = estimate?.cost || 0;
                const numLangs = (effectiveTargets && effectiveTargets.length) || 1;
                const costDisplay = cost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });

                return (
                  <>
                    This request will cost <b>
                      {cost < 0.01 ? "0.01" : costDisplay} credits
                    </b> for <b>{numLangs}</b> language{numLangs !== 1 ? "s" : ""}.
                    <br />
                    <b>Select the "Translate" button to accept and proceed with the translation.</b>
                  </>
                );
              })()
            )}
          </div></>
      )}

      {estimate && estimate.error && (
        <div data-testid="translate-estimate-failure" className="text-red-600">{estimate.error}</div>
      )}

      </>
    )}

      

      {/* Download button and Uploading... message */}

      {showFilesUploading && (
        <div data-testid="translate-uploading-files" className="flex flex-col items-center mt-2">
          <div
            className="w-6 h-6 border-4 border-t-transparent rounded-full animate-spin mb-2"
            style={{ borderColor: "#00a7e1", borderTopColor: "transparent" }}
          ></div>
          <div className="text-sm" style={{ color: "#00a7e1" }}>
            Translated files uploading. This may take a few minutes. Please wait{".".repeat(dotCount)}
				<br />
				If you don't want to wait, you can also close this page and you will receive an e-mail when the upload completes.
          </div>
        </div>
      )}
      
      {downloadReady && lastEstimate && !wasCancelled && downloadUrl && (
        <>
          <div className="flex items-center justify-center gap-2 mt-3 text-lg font-semibold" style={{ color: "#00a7e1" }}>
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-full"
              style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}
              aria-hidden="true"
            >
              ✓
            </span>
            <span data-testid="translate-complete-message">Translations are complete! Download files below.</span>
          </div>

          <a
            data-testid="translate-download"
            href={downloadUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="text-white px-4 py-2 rounded hover:opacity-90 w-full mt-2 text-lg font-semibold flex items-center justify-center"
            style={{ backgroundColor: "#94c83d" }}
            onClick={() => {
              setTimeout(() => {
                resetRequestState();
              }, 800);
				}}
          >
            Download Files
          </a>
        </>
      )}
        
{/* Animation + Progress Bar */}
      {translationError ? (
        <div className="mt-4">
          <div data-testid="translate-error" className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center font-semibold">
            {translationError}
          </div>
        </div>
      ) : uploading && (
        <div data-testid="translate-progress" className="mt-4 space-y-2">
          <div className="flex justify-center">
            <img
              src="/aitranslation.gif"
              alt="Translating..."
              className="w-24 h-24 sm:w-28 sm:h-28"
            />
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="h-4 rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, backgroundColor: "#94c83d" }}
            ></div>
          </div>

          {(() => {
            const raw = progressMessage || "";
            // If we have the two-line format: "[x files × y languages]<br/>Translating ... 68%"
            if (raw.includes("<br/>")) {
              const [topRaw, ...rest] = raw.split("<br/>");
              let bottomRaw = rest.join("<br/>");

              // Pull percentage (first match) from the bottom line
              const pctMatch = bottomRaw.match(/(\d{1,3})%/);
              const pct = pctMatch ? pctMatch[1] + "%" : null;

              // Remove the percentage from the bottom line (just the first occurrence)
              if (pct) {
                bottomRaw = bottomRaw.replace(/\s*\d{1,3}%/, "");
              }

              // Blink the dots on both lines
              const topBlink =
                topRaw
                  .replace(/\u2026/g, "...")
                  .replace(/\.{3}/g, ".".repeat(dotCount)) +
                (pct ? ` ${pct}` : "");

              const bottomBlink = bottomRaw
                .replace(/\u2026/g, "...")
                .replace(/\.{3}/g, ".".repeat(dotCount))
                .trim();

              return (
                <>
                    <div data-testid="translate-progress-summary" className="text-sm text-center" style={{ margin: 0, lineHeight: 1.2 }}>{topBlink}</div>
                    <div data-testid="translate-progress-message" className="text-sm text-left" style={{ margin: 0, lineHeight: 1.2 }}>{bottomBlink}</div>
                </>
              );
            }

            // Fallback: single-line message (just left-justify with blinking dots)
            const singleBlink = raw
              .replace(/\u2026/g, "...")
              .replace(/\.{3}/g, ".".repeat(dotCount));
            return <p data-testid="translate-progress-message" className="text-sm text-left">{singleBlink}</p>;
          })()}

          <p className="text-xs text-center font-bold" style={{ color: "#00a7e1" }}>
            The translation process may take some time depending on the number of languages, files, and file sizes. 
            If you don't want to wait, you can close this window and an e-mail will be sent to you when the files are ready for download.
          </p>

          {!isCancelled && (
            <button
              data-testid="translate-cancel"
              onClick={handleCancel}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 w-full mt-2"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Wait message while estimation cost is calculating */}
      {estimating && (
        <div data-testid="translate-calculating" className="text-center mb-2 font-semibold" style={{ color: "#00a7e1" }}>
          {`Calculating cost, please wait${".".repeat(dotCount)}`}
        </div>
      )}

      {/* Show "Translate" only when not uploading */}
      {!uploading && !downloadReady && xliffPrompts.length === 0 && (() => {
        const customOn = !!(user && user.uses_custom_workflow);
        const pemtLabel = String(workflowPEMT || "").toLowerCase();
        const fmtLabel  = String(workflowFormatting || "").toLowerCase();
        const off = new Set(["none","no","disabled","false"]);
        const pemtOn = customOn && pemtLabel && !off.has(pemtLabel);
        const fmtOn  = customOn && fmtLabel  && !off.has(fmtLabel);

        const label = (pemtOn || fmtOn) ? "Accept Estimate" : "Translate";

        const disabled = uploading || estimating || !estimate || !!estimate.error;

        // Only bold "Accept Estimate" once enabled
        const fontClass = (label === "Accept Estimate" && disabled) ? "" : "font-bold";

        return (
          <button
            data-testid="translate-submit"
            onClick={handleSubmit}
            disabled={disabled}
            className={`text-white ${fontClass} px-4 py-2 rounded w-full ${disabled ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
            style={{ backgroundColor: "#94c83d" }}
          >
            {uploading
              ? `Translating${".".repeat(dotCount)}`
              : estimating
                ? `Calculating Cost${".".repeat(dotCount)}`
                : label}
          </button>
        );
      })()}
      
	  {/* Professional services offer */}
      {!user?.uses_custom_workflow ? (
        <div className="mt-2 text-center space-y-2">
          <p className="text-sm font-bold" style={{ color: "#00a7e1" }}>
            Don&apos;t like the results?
          </p>
          <button
            data-testid="translate-convert-custom"
            type="button"
            className="text-white px-4 py-2 rounded hover:opacity-90 font-semibold"
            style={{ backgroundColor: "#00a7e1" }}
            onClick={() => { if (typeof onOpenAiRequestsForConvert === "function") onOpenAiRequestsForConvert(); }}
          >
            Convert to Custom Request
          </button>
          <p className="text-sm font-bold" style={{ color: "#00a7e1" }}>
            Or try our{" "}
            <a
              data-testid="translate-professional-services"
              href="https://zabtranslation.com/contactquote.html"
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              professional translation services
            </a>.
          </p>
        </div>
      ) : (
        <p className="text-sm text-center font-bold mt-2" style={{ color: "#00a7e1" }}>
          Don&apos;t like the results? Try our{" "}
          <a
            href="https://zabtranslation.com/contactquote.html"
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            professional translation services
          </a>.
        </p>
      )}

      {/* Ensure credits always re-render by referencing creditsTick */}
      <span style={{display:"none"}}>{creditsTick}</span>
    </div>
  );
}

function AppUI() {
  const navigate = useNavigate();
  const location = useLocation();
  // Hide App-level pre-login header/CTA on the root (LoginForm) page
  const atRoot = location.pathname === "/";  
  
  const { user, logout, loading, pendingConfirmation, setPendingConfirmation, pendingEmail, setPendingEmail, setAuthError, creditsTick, forceReset, setForceReset, jwt, setUserAndTick } = useAuth();
  const [showRegister, setShowRegister] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("main"); // "main" or "admin"

  // === AI Requests (History) ===
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const historyLoadedOnceRef = useRef(false);
  const [aiConvertItem, setAiConvertItem] = useState(null);

  React.useEffect(() => {
    if (activeTab !== "aiRequests" && aiConvertItem) {
      setAiConvertItem(null);
    }
  }, [activeTab, aiConvertItem]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/history`,
        { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
      );
      const raw = res.data;
      const items = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
      // History excludes custom workflow quote requests on the backend.
      setHistory(items);
      historyLoadedOnceRef.current = true;
    } catch (e) {
      setHistory([]);
      alert("Failed to load history");
    }
    setHistoryLoading(false);
  };

  const handleDownload = async (taskId) => {
    try {
      const res = await axios.get(
        `${API_BASE}/download/${taskId}`,
        { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
      );

      // Case A: backend returns { url: "<presigned S3 url>" }
      const url = res?.data?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      // Case B (fallback): backend streams the file directly
      if (res?.data && (res.request?.responseType === "blob" || typeof res.data === "object")) {
        const blob = new Blob([res.data]);
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `translation_${taskId}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        return;
      }

      alert("Download link not available yet. Please try again shortly.");
    } catch (e) {
      alert("Failed to fetch download link. Please try again.");
    }
  };


  const openAiRequestConvert = (item) => {
    setAiConvertItem(item || null);
    safeSwitchTab("aiRequests");
    if (!historyLoadedOnceRef.current) fetchHistory();
  };

  const [homeResetNonce, setHomeResetNonce] = useState(0); // increments when Home/logo clicked while already on Home (resets Thank You screen)
  const [settingsView, setSettingsView] = useState("main"); // "main" | "terminology" | "tmx" | "workflowEdit"
  const [workflowDirty, setWorkflowDirty] = useState(false); // tracks unsaved changes in Custom Workflow
  const [modeSwitching, setModeSwitching] = useState(false); // prevents duplicate workflow toggle clicks

  // Map tabs/subviews to URL hashes (must be defined before any effect uses them)
  const TAB_HASH = {
    main: "#home",
    projectRequests: "#project-requests",
    aiRequests: "#ai-requests",
    help: "#help",
    feedback: "#feedback",
    about: "#about",
    settings: "#settings",
    admin: "#admin",
    diagnostics: "#diagnostics",
  };
  const SUB_HASH = {
    workflowEdit: "#custom-workflow",
    terminology: "#terminology",
    tmx: "#tmx",
    main: "#settings",
  };
  const HASH_TO_STATE = (hash) => {
    switch (hash) {
      case "#home": return { tab: "main", view: "main" };
      case "#project-requests": return { tab: "projectRequests", view: "main" };
      case "#ai-requests": return { tab: "aiRequests", view: "main" };
      case "#help": return { tab: "help", view: "main" };
      case "#feedback": return { tab: "feedback", view: "main" };
      case "#about": return { tab: "about", view: "main" };
      case "#settings": return { tab: "settings", view: "main" };
      case "#custom-workflow": return { tab: "settings", view: "workflowEdit" };
      case "#terminology": return { tab: "settings", view: "terminology" };
      case "#tmx": return { tab: "settings", view: "tmx" };
      case "#admin": return { tab: "admin", view: "main" };
      case "#diagnostics": return { tab: "diagnostics", view: "main" };
      default: return { tab: "main", view: "main" };
    }
  };

  // Open Registration if URL has ?register=1 (from LoginClassic link)
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('register') === '1') {
        setShowRegister(true);
        // Clean up the URL without reloading
        const url = new URL(window.location.href);
        url.searchParams.delete('register');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (_) {}
    // run once
	/* eslint-disable-next-line */
  }, []);

  // Sync UI with URL hash on load and when user presses Back/Forward
  React.useEffect(() => {
    const applyFromHash = () => {
      // If the URL includes a specific quote token, force the Project Requests tab.
      // This allows the quote link from email to open directly inside the main app UI.
      try {
        const params = new URLSearchParams(window.location.search || "");
        if (params.get("quote")) {
          setActiveTab("projectRequests");
          setSettingsView("main");
          return;
        }
if (params.get("tab")) {
          const rawTab = (params.get("tab") || "").toString().trim();
          const rawView = (params.get("view") || "").toString().trim();

          // Normalize common tab aliases -> internal tab keys
          const tabKey = rawTab.toLowerCase();
          let nextTab = rawTab;

          if (tabKey === "translate" || tabKey === "home" || tabKey === "main") nextTab = "main";
          else if (
            tabKey === "custom" ||
            tabKey === "customrequests" ||
            tabKey === "projectrequests" ||
            tabKey === "project-requests"
          )
            nextTab = "projectRequests";
          else if (tabKey === "ai" || tabKey === "airequests" || tabKey === "ai-requests") nextTab = "aiRequests";
          else if (tabKey === "help") nextTab = "help";
          else if (tabKey === "feedback" || tabKey === "sendfeedback") nextTab = "feedback";
          else if (tabKey === "about" || tabKey === "aboutzab") nextTab = "about";
          else if (tabKey === "settings") nextTab = "settings";
          else if (tabKey === "admin") nextTab = "admin";
          else if (tabKey === "diagnostics" || tabKey === "pdfdiagnostics") nextTab = "diagnostics";

          setActiveTab(nextTab);

          // Optional settings view support: /?tab=settings&view=terminology|tmx|main
          if (nextTab === "settings" && rawView) {
            setSettingsView(rawView);
          } else {
            setSettingsView("main");
          }

          return;
        }
      } catch (_) {}

      const { tab, view } = HASH_TO_STATE(window.location.hash || "#home");
      setActiveTab(tab);
      setSettingsView(view);
    };
    applyFromHash();

    const onPop = () => applyFromHash();
    window.addEventListener("popstate", onPop);
    window.addEventListener("hashchange", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("hashchange", onPop);
    };
    // run once
    // eslint-disable-next-line
  }, []);

  const handlePendingConfirmation = (email) => {
    setPendingEmail(email);
    setPendingConfirmation(true);
    setShowRegister(false);
  };
  const handleGoToLogin = () => {
    setPendingConfirmation(false);
    setShowRegister(false);
    setAuthError("");
    navigate("/signin");
  };

  // Push a new history entry with the right hash
  const pushHash = (tab, view = "main") => {
    let hash = TAB_HASH[tab] || "#home";
    if (tab === "settings") {
      hash = SUB_HASH[view] || "#settings";
    }
    if (window.location.hash !== hash) {
      window.history.pushState({ tab, view }, "", hash);
    }
  };

  const safeSwitchTab = (nextTab) => {
    // Confirm if leaving Custom Workflow with unsaved changes
    if (activeTab === "settings" && settingsView === "workflowEdit" && workflowDirty) {
      const leave = window.confirm(
        "You have unsaved changes in your custom workflow. If you leave now, your changes will be discarded. Do you want to leave without saving?"
      );
      if (!leave) return;
    }
    pushHash(nextTab, "main");
    setActiveTab(nextTab);
    setSettingsView("main");
  };

  const goToCustomWorkflowEditor = () => {
    pushHash("settings", "workflowEdit");
    setActiveTab("settings");
    setSettingsView("workflowEdit");
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  };

  const handleModeToggle = async (nextUsesCustomWorkflow) => {
    if (!user || modeSwitching) return;
    if (!!user.uses_custom_workflow === !!nextUsesCustomWorkflow) return;

    // If leaving Custom Workflow while editing unsaved settings, confirm first.
    if (
      user.uses_custom_workflow &&
      !nextUsesCustomWorkflow &&
      activeTab === "settings" &&
      settingsView === "workflowEdit" &&
      workflowDirty
    ) {
      const leave = window.confirm(
        "You have unsaved changes in your custom workflow. If you leave now, your changes will be discarded. Do you want to switch modes without saving?"
      );
      if (!leave) return;
    }

    setModeSwitching(true);

    try {
      const r = await fetch(`${API_BASE}/workflow/mode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ uses_custom_workflow: !!nextUsesCustomWorkflow }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data?.detail || `Request failed (${r.status})`);
      }

      // Refresh /me so the whole app reflects the new mode immediately.
      let nextUser = null;
      try {
        const me = await fetch(`${API_BASE}/me`, {
          headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
          credentials: "include",
        });
        if (me.ok) {
          nextUser = await me.json();
        }
      } catch (_) {}

      if (!nextUser) {
        nextUser = { ...user, uses_custom_workflow: !!nextUsesCustomWorkflow };
      }

      if (typeof setUserAndTick === "function") {
        setUserAndTick(nextUser);
      }

      if (nextUsesCustomWorkflow) {
        goToCustomWorkflowEditor();
      } else {
        safeSwitchTab("main");
      }
    } catch (e) {
      console.error("Workflow mode toggle failed:", e);
      alert("Unable to change workflow mode right now. Please try again.");
    } finally {
      setModeSwitching(false);
    }
  };

  const modeOptionClass = (isActive) =>
    `px-3 py-1 rounded-full text-sm font-semibold border transition ${isActive ? "text-white shadow-sm" : "bg-white hover:opacity-90"}`;

  const modeOptionStyle = (isActive) =>
    isActive
      ? { backgroundColor: "#00a7e1", borderColor: "#00a7e1" }
      : { color: "#00a7e1", borderColor: "#00a7e1" };

  const modeTrackStyle = {
    backgroundColor: "#e5e7eb",
    width: "42px",
    minWidth: "42px",
    height: "22px",
    borderRadius: "9999px",
    position: "relative",
    transition: "background-color 0.2s ease",
  };

  const modeKnobStyle = {
    position: "absolute",
    top: "2px",
    left: user?.uses_custom_workflow ? "2px" : "22px",
    width: "18px",
    height: "18px",
    borderRadius: "9999px",
    backgroundColor: "#00a7e1",
    transition: "left 0.2s ease",
  };

  if (forceReset) {
    return (
      <ResetPassword
        onGoToLogin={() => {
          setForceReset(false); // Clear flag after reset
          setShowResetPassword(false);
          logout(); // Optionally force logout
        }}
      />
    );
  }
  
  return (
    <div>
      {!user && !atRoot && !showRegister && !showResetPassword && !pendingConfirmation && <PublicHeader />}

      {loading && <div>Loading...</div>}
      {pendingConfirmation && <EmailConfirmation onGoToLogin={handleGoToLogin} />}
      {showResetPassword ? (
        <ResetPassword onGoToLogin={() => { setShowResetPassword(false); }} />
      ) : !loading && !pendingConfirmation && !user && (
         <div>
          {showRegister
            ? <RegistrationForm
                onPendingConfirmation={handlePendingConfirmation}
                 onGoToLogin={handleGoToLogin}
              />
            : <LoginForm
                onPendingConfirmation={handlePendingConfirmation}
                onForgotPassword={() => setShowResetPassword(true)}
                onGoToRegister={() => { setShowRegister(true); setAuthError(""); }}
              />
          }
        </div>
      )}

            {user && (
        <>
          {/* Post-login header row: small logo (left) + user/credits/actions (right) */}
          <div className="pt-3 mb-4 px-4">
            <div className="flex items-center justify-between">

              {/* Left: compact logo (click = go to Home/main) */}
              <button
                type="button"
                onClick={() => {
                  if (activeTab === "main") {
                    setHomeResetNonce((n) => n + 1);
                  }
                  safeSwitchTab("main");
                  if (typeof window !== "undefined") window.scrollTo(0, 0);
                }}
                className="flex items-center focus:outline-none"
                aria-label="Go to Home"
              >
                <img
                  src="/zabflow2.png"
                  alt="ZabFlow AI™ Logo"
                  className="h-12 md:h-14 w-auto select-none cursor-pointer"
                />
              </button>

              {/* Right: user info + credits + actions */}
              <div className="text-right pr-2">
                <div>
                <div>
                  Logged in as: <b>{user.email}</b>
                  <button
                    type="button"
                    onClick={() => {
                      safeSwitchTab("settings");
                    }}
                    aria-label="Open Settings"
                    title="Settings"
                    className="align-middle inline-block ml-2 focus:outline-none"
                  >
                    <img
                      src="/settings.png"
                      alt=""
                      className="h-5 w-5 inline-block cursor-pointer transform -translate-y-[2px]"
                      draggable="false"
                    />
                  </button>

                  {/* Current Mode line directly under the email (same size as "Logged in as") */}
                  <div className="text-gray-700 flex items-center justify-end gap-2 flex-wrap mb-2">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleModeToggle(true)}
                        disabled={modeSwitching || !!user?.uses_custom_workflow}
                        className={`${modeOptionClass(!!user?.uses_custom_workflow)} ${modeSwitching || !!user?.uses_custom_workflow ? "cursor-default" : ""}`}
                        style={modeOptionStyle(!!user?.uses_custom_workflow)}
                        aria-pressed={!!user?.uses_custom_workflow}
                      >
                        Custom Workflow
                      </button>

                      <button
                        type="button"
                        onClick={() => handleModeToggle(!user?.uses_custom_workflow)}
                        disabled={modeSwitching}
                        className={`inline-flex items-center justify-center border ${modeSwitching ? "cursor-default opacity-70" : "cursor-pointer"}`}
                        style={modeTrackStyle}
                        aria-label={`Switch to ${user?.uses_custom_workflow ? "Generic Workflow" : "Custom Workflow"}`}
                        title={`Switch to ${user?.uses_custom_workflow ? "Generic Workflow" : "Custom Workflow"}`}
                      >
                        <span style={modeKnobStyle} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleModeToggle(false)}
                        disabled={modeSwitching || !user?.uses_custom_workflow}
                        className={`${modeOptionClass(!user?.uses_custom_workflow)} ${modeSwitching || !user?.uses_custom_workflow ? "cursor-default" : ""}`}
                        style={modeOptionStyle(!user?.uses_custom_workflow)}
                        aria-pressed={!user?.uses_custom_workflow}
                      >
                        Generic Workflow
                      </button>
                    </div>
                  </div>

                  <span>
                    Credits Remaining:{" "}
                    <b>
                      🪙{(user.credits ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </b>
                    <a
                      href="/billing"
                      className="ml-3 inline-block text-white px-3 py-1 rounded hover:opacity-90"
                      style={{ verticalAlign: "middle", backgroundColor: "#94c83d" }}
                    >
                      Buy Credits
                    </a>
                  </span>
                </div>
               </div>
                <button
                  onClick={logout}
                  className="underline mt-1"
                  style={{ color: "#00a7e1" }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Public tab navigation (visible to all users) */}
          <div className="flex justify-start mb-4 gap-2 pl-4">
            <button
              className={`px-4 py-2 rounded-t ${activeTab === "main" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
              style={{ backgroundColor: activeTab === "main" ? "#00a7e1" : "#f3f4f6" }}
              onClick={() => {
                setHomeResetNonce((n) => n + 1);
                safeSwitchTab("main");
              }}
            >
              Translate
            </button>
<button
  className={`px-4 py-2 rounded-t ${activeTab === "projectRequests" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
  style={{ backgroundColor: activeTab === "projectRequests" ? "#00a7e1" : "#f3f4f6" }}
  onClick={() => {
    safeSwitchTab("projectRequests");
  }}
>
  Custom Requests
</button>
<button
  className={`px-4 py-2 rounded-t ${activeTab === "aiRequests" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
  style={{ backgroundColor: activeTab === "aiRequests" ? "#00a7e1" : "#f3f4f6" }}
  onClick={() => {
    safeSwitchTab("aiRequests");
    // Load history when opening the tab
    if (!historyLoadedOnceRef.current) fetchHistory();
  }}
>
  A.I. Requests
</button>
            <button
              className={`px-4 py-2 rounded-t ${activeTab === "help" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
              style={{ backgroundColor: activeTab === "help" ? "#00a7e1" : "#f3f4f6" }}
             onClick={() => {
			    safeSwitchTab("help");
			  }}
            >
              Help
            </button>
            <button
              className={`px-4 py-2 rounded-t ${activeTab === "feedback" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
              style={{ backgroundColor: activeTab === "feedback" ? "#00a7e1" : "#f3f4f6" }}
             onClick={() => {
			    safeSwitchTab("feedback");
			  }}
            >
              Send Feedback
            </button>
            <button
              className={`px-4 py-2 rounded-t ${activeTab === "about" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
              style={{ backgroundColor: activeTab === "about" ? "#00a7e1" : "#f3f4f6" }}
             onClick={() => {
			    safeSwitchTab("about");
			  }}
            >
              About Zab
            </button>
            <button
              className={`px-4 py-2 rounded-t ${activeTab === "settings" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
              style={{ backgroundColor: activeTab === "settings" ? "#00a7e1" : "#f3f4f6" }}
            onClick={() => {
			    safeSwitchTab("settings");
			  }}
            >
              Settings
            </button>
          </div>

          {/* Admin and PDF Diagnostics tab navigation */}
          {(user.is_admin || user.is_super_admin || user.is_assistant) && (
            <div className="flex justify-start mb-4 gap-2 pl-4">
              <button
                className={`px-4 py-2 rounded-t ${activeTab === "admin" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
                style={{ backgroundColor: activeTab === "admin" ? "#00a7e1" : "#f3f4f6" }}
                onClick={() => {
			      safeSwitchTab("admin");
			    }}
              >
                Admin Panel
              </button>
                            {(user.is_admin || user.is_super_admin) && (
<button
                className={`px-4 py-2 rounded-t ${activeTab === "diagnostics" ? "font-semibold text-white" : "bg-gray-100 hover:opacity-90"}`}
                style={{ backgroundColor: activeTab === "diagnostics" ? "#00a7e1" : "#f3f4f6" }}
                onClick={() => {
			      safeSwitchTab("diagnostics");
			    }}
              >
                PDF Diagnostics
              </button>
              )}
            </div>
          )}

          {/* Render panels based on activeTab */}
          <div>
            {activeTab === "help" ? (
              <Help />
            ) : activeTab === "feedback" ? (
              <SendFeedback />
            ) : activeTab === "about" ? (
              <AboutZab />
            ) : activeTab === "projectRequests" ? (
              <ProjectRequests API_BASE={API_BASE} />
            ) : activeTab === "aiRequests" ? (
              <AIRequests
                history={history}
                historyLoading={historyLoading}
                onRefresh={fetchHistory}
                onDownload={handleDownload}
                onOpenConvertToCustomRequest={openAiRequestConvert}
                selectedConvertItem={aiConvertItem}
                onCloseConvertToCustomRequest={() => setAiConvertItem(null)}
                onConversionSubmitted={(taskId) => {
                  setAiConvertItem(null);
                  fetchHistory();
                }}
                API_BASE={API_BASE}
                jwt={jwt}
              />
            ) : activeTab === "admin" && (user.is_admin || user.is_super_admin || user.is_assistant) ? (
              <AdminPanel />
            ) : activeTab === "settings" ? (
              settingsView === "terminology" ? (
                <TerminologySettings onBack={() => { setSettingsView("workflowEdit"); pushHash("settings", "workflowEdit"); }} />
              ) : settingsView === "tmx" ? (
                <TMXSettings onBack={() => { setSettingsView("workflowEdit"); pushHash("settings", "workflowEdit"); }} />
              ) : settingsView === "workflowEdit" ? (
                <CustomWorkflow
                  onBack={() => { setSettingsView("main"); pushHash("settings", "main"); }}
                  onOpenTerminologySettings={() => { setSettingsView("terminology"); pushHash("settings", "terminology"); }}
                  onOpenTMXSettings={() => { setSettingsView("tmx"); pushHash("settings", "tmx"); }}
                  onDirtyChange={setWorkflowDirty}
                />
              ) : (

                <Settings
                  user={user}
                  jwt={jwt}
                  apiBase={API_BASE}
                  onOpenTerminologySettings={() => { setSettingsView("terminology"); pushHash("settings", "terminology"); }}
                  onOpenWorkflowBuilder={() => { setSettingsView("workflowEdit"); pushHash("settings", "workflowEdit"); }}
                  onOpenTMXSettings={() => { setSettingsView("tmx"); pushHash("settings", "tmx"); }}
                />

              )
            ) : activeTab === "diagnostics" && (user.is_admin || user.is_super_admin) ? (

              <PDFDiagnostics jwt={jwt} />
            ) : (
              <TranslationUI homeResetNonce={homeResetNonce} onOpenAiRequestsForConvert={() => openAiRequestConvert(null)} />
            )}
          </div>

        </>
      )}
    </div>
  );
}


function ProjectApproval() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [data, setData] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    setErr("");
    axios
      .get(`${API_BASE}/project-approval/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setErr("Unable to load project approval."))
      .finally(() => setLoading(false));
  }, [token]);

  const decide = async (decision) => {
    if (!token || submitting) return;
    setSubmitting(true);
    setErr("");
    try {
      await axios.post(`${API_BASE}/project-approval/${token}/decision`, { decision });
      const res = await axios.get(`${API_BASE}/project-approval/${token}`);
      setData(res.data);
    } catch {
      setErr("Unable to submit your decision.");
    } finally {
      setSubmitting(false);
    }
  };

  const filesLabel = (() => {
    const files = data?.source_filenames;
    if (Array.isArray(files) && files.length === 1) return files[0];
    if (Array.isArray(files) && files.length > 1) return `${files.length} Files`;
    return "—";
  })();

  const languagesLabel = (() => {
    const langs = data?.languages;
    if (Array.isArray(langs) && langs.length === 1) return langs[0];
    if (Array.isArray(langs) && langs.length > 1) return `${langs.length} Languages`;
    return "—";
  })();

  const quoteReady =
    data && (Array.isArray(data.final_cost_breakdown) && data.final_cost_breakdown.length) && data.final_total;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button className="font-bold" onClick={() => navigate("/")}>
            ZabFlow
          </button>
          <div className="text-sm text-gray-600">Project Quote</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow p-6">
          {loading ? (
            <div className="text-sm text-gray-600">Loading…</div>
          ) : err ? (
            <div className="text-sm text-red-600">{err}</div>
          ) : !data ? (
            <div className="text-sm text-gray-600">Not found.</div>
          ) : (
            <>
              <div className="mb-4">
                <div className="text-xl font-semibold">Your translation quote</div>
                <div className="text-sm text-gray-600 mt-1">
                  Review the costs below and choose Accept or Reject.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="border rounded-xl p-3">
                  <div className="text-sm text-gray-600">Files</div>
                  <div className="text-sm font-medium break-words">{filesLabel}</div>
                </div>
                <div className="border rounded-xl p-3">
                  <div className="text-sm text-gray-600">Languages</div>
                  <div className="text-sm font-medium break-words">{languagesLabel}</div>
                </div>
                <div className="border rounded-xl p-3">
                  <div className="text-sm text-gray-600">Estimated Word Count</div>
                  <div className="text-sm font-medium">
                    {typeof data.estimated_word_count === "number"
                      ? data.estimated_word_count.toLocaleString()
                      : "—"}
                  </div>
                </div>
                <div className="border rounded-xl p-3">
                  <div className="text-sm text-gray-600">Status</div>
                  <div className="text-sm font-medium">{data.status || "—"}</div>
                </div>
              </div>

              {data.estimated_completion_date ? (
                <div className="mb-4">
                  <div className="text-sm text-gray-600">Estimated Completion Date</div>
                  <div className="text-sm">{data.estimated_completion_date}</div>
                </div>
              ) : null}

              <div className="mb-5">
                <div className="text-sm text-gray-600">Final Costs</div>
                {Array.isArray(data.final_cost_breakdown) && data.final_cost_breakdown.length ? (
                  <ul className="text-sm mt-1 space-y-1">
                    {data.final_cost_breakdown.map((c, i) => (
                      <li key={i} className="flex gap-2">
                        <span>•</span>
                        <span className="break-words">{c}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm mt-1">—</div>
                )}
                <div className="mt-2 font-semibold">Total: {data.final_total || "—"}</div>
              </div>

              {data.user_decision && data.user_decision !== "pending" ? (
                <div className="text-sm font-medium">
                  {data.user_decision === "accepted"
                    ? "Thanks! Your quote has been accepted."
                    : "Your quote has been rejected. If you need changes, please reply to the email you received."}
                </div>
              ) : !quoteReady ? (
                <div className="text-sm text-gray-700">
                  This quote isn’t ready yet. Please check back from the email link once costs have been submitted.
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    className="px-4 py-2 rounded-lg font-semibold text-white"
                    style={{ backgroundColor: "#94c83d" }}
                    disabled={submitting}
                    onClick={() => decide("accepted")}
                  >
                    {submitting ? "Submitting…" : "Accept"}
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg font-semibold border"
                    disabled={submitting}
                    onClick={() => decide("rejected")}
                  >
                    Reject
                  </button>
                </div>
              )}

              <div className="mt-6 text-xs text-gray-500">
                Questions? Contact Zab at{" "}
                <a
                  href="https://zabtranslation.com/contact.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-bold"
                  style={{ color: "#00a7e1" }}
                >
                  zabtranslation.com
                </a>
                .
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}



export default function App() {
  // Load Zendesk globally (runs once for all routes)
  useEffect(() => {
    const script = document.createElement("script");
    script.id = "ze-snippet";
    script.src =
      "https://static.zdassets.com/ekr/snippet.js?key=388da20d-6069-49f2-87c0-e2b7d9f26278";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("ze-snippet");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <AuthProvider>
        {/* Wrap entire app layout so footer sits at bottom */}
        <div className="flex flex-col min-h-screen">
          <div className="flex-grow">
            <Routes>
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/confirm-email" element={<EmailConfirmation />} />
			  <Route path="/help" element={<Help />} />
              <Route path="/about" element={<AboutZab />} />
              <Route path="/project-approval/:token" element={<ProjectApproval />} />
              <Route path="/*" element={<AppUI />} />
			  <Route path="/billing" element={<PurchaseCredits />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/project-payment/success" element={<ProjectPaymentSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
			  <Route path="/admin/payments" element={<PaymentTracking />} />
              <Route path="/workflow-builder" element={<WorkflowBuilder />} />
              <Route
                path="/register"
                element={
                  <RegistrationForm
                    onPendingConfirmation={() => {}}
                    onGoToLogin={() => window.location.assign("/signin")}
                  />
                }
              />
			  <Route path="/signin" element={<LoginClassic />} />
			  <Route path="/login" element={<LoginClassic />} />
            </Routes>
          </div>

          {/* Global footer visible on all pages */}
          <footer className="py-4 text-center text-xs text-gray-500 border-t border-gray-200">
            <p className="mb-1">
              <a
                href="https://zabtranslation.com/terms-and-privacy.html"
                className="underline"
				style={{ color: "#00a7e1" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Privacy
              </a>
              {'\u00A0'.repeat(8)} {/* exactly 8 non-breaking spaces */}
              <a
                href="https://zabtranslation.com/contact.html"
                className="underline text-xs font-bold"
				style={{ color: "#00a7e1" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact Us
              </a>
            </p>
            <p>Copyright © Zab, LLC 2012–2026. All rights reserved.</p>
          </footer>
        </div>
    </AuthProvider>
  );
}
